import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS, API_BASE_URL, apiClient } from '../config/api';
import { auth } from '../firebase/config';
import { getActiveRole, getActiveRoleFromStorage } from '../utils/getActiveRole';
import { hasFormAccess, hasStateFormLibrary } from '../utils/docusignFormAccess';
import { getViewerTimezone, nowWallClockInViewerZone } from '../utils/dateTime';
import { formatRiaMessage } from '../utils/formatRiaMessage';
import AskRiaInputBar from '../components/common/AskRiaInputBar';
import './DashboardPage.css';
// Reused for the .spd-profile-* classes rendered by
// renderProfileCompletionCard below (agent/broker parity with the SP
// dashboard's Complete Your Profile card). All rules in that file are
// prefixed with .spd-* so importing it here doesn't collide with the
// .dashboard-* classes above.
import './SpDashboardPage.css';
import transactionsIcon from '../assets/quick-action/buying.png';
import listingIcon from '../assets/quick-action/List.png';
import inviteIcon from '../assets/quick-action/invite-someone.png';
import keyIcon from '../assets/quick-action/Keys.png';
import bulbIcon from '../assets/bulb.png';
import serviceIcon from '../assets/quick-action/Setting.png';
import docusignIcon from '../assets/docusign-icon.svg';
import riaLogo from '../assets/center-logo.png';
import RiaNoteLinks, { extractRiaNoteLinks } from '../components/notes/RiaNoteLinks';
import CmaReportLink, { extractCmaReportLink } from '../components/common/CmaReportLink';

// How close to the top (in px) of the Ria chat area triggers the next older-history
// fetch — both the IntersectionObserver's rootMargin and the post-restore chain-check
// below use this same value, so they agree on what "near the top" means.
const RIA_HISTORY_LOOKAHEAD_PX = 60;

const RIA_MONTH_ABBREV = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Per-message "date · time" label — mirrors the mobile app's RiaChatScreen.formatTime
// (abbreviated month/day, year only when it differs from the current one) so a
// message's timestamp is always visible without needing to scroll to trigger the
// day-group pill above.
const formatRiaMessageTimestamp = (date) => {
  const zone = getViewerTimezone();
  const time = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: zone });
  const parts = {};
  new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'numeric', day: 'numeric', timeZone: zone })
    .formatToParts(date).forEach((p) => { if (p.type !== 'literal') parts[p.type] = p.value; });
  const nowYear = new Intl.DateTimeFormat('en-US', { year: 'numeric', timeZone: zone }).format(new Date());
  const sameYear = parts.year === nowYear;
  const monthIdx = Number(parts.month) - 1;
  const datePart = sameYear
    ? `${RIA_MONTH_ABBREV[monthIdx]} ${parts.day}`
    : `${RIA_MONTH_ABBREV[monthIdx]} ${parts.day}, ${parts.year}`;
  return `${datePart} · ${time}`;
};

// Teams/WhatsApp-style date label: "Today", "Yesterday", or "Month D, YYYY".
const riaDateLabel = (date) => {
  const zone = getViewerTimezone();
  const dayKey = (d) => d.toLocaleDateString('en-CA', { timeZone: zone });
  const todayKey = dayKey(new Date());
  const targetKey = dayKey(date);
  if (targetKey === todayKey) return 'Today';
  if (dayKey(new Date(Date.now() - 86400000)) === targetKey) return 'Yesterday';
  return date.toLocaleDateString([], { month: 'long', day: 'numeric', year: 'numeric', timeZone: zone });
};

// Groups a flat, chronologically-ordered message list into day buckets so the
// UI can render one sticky date header per day (position:sticky handles the
// "floating overlay while scrolling" behavior with no manual scroll math).
const groupRiaMessagesByDay = (messages) => {
  const groups = [];
  let current = null;
  messages.forEach((msg) => {
    const d = msg.createdAt instanceof Date && !isNaN(msg.createdAt) ? msg.createdAt : null;
    const dateKey = d ? d.toLocaleDateString('en-CA', { timeZone: getViewerTimezone() }) : 'unknown';
    if (!current || current.dateKey !== dateKey) {
      current = { dateKey, label: d ? riaDateLabel(d) : '', items: [] };
      groups.push(current);
    }
    current.items.push(msg);
  });
  return groups;
};

// Mirrors the mobile app's RiaChatScreen formatPropertyPrice — abbreviates
// to "$3.00M" / "$648K" for the property result cards under a Ria reply.
const formatRiaPropertyPrice = (price) => {
  const n = Number(price) || 0;
  if (n >= 1000000) return `$${(n / 1000000).toFixed(2)}M`;
  return `$${(n / 1000).toFixed(0)}K`;
};

// Transforms a raw MLS object (as returned inside structured_data.data.properties)
// into the flat shape the property-card UI reads. Mirrors PropertiesPage.js's
// transformMLSProperty / mobile's property.service.ts transformMLSProperty.
const transformRiaMlsProperty = (mls) => {
  if (!mls || typeof mls !== 'object') return null;

  const address = mls.address || {};
  const property = mls.property || {};
  const mlsInfo = mls.mls || {};

  let status = null;
  let statusColor = '#2E7D32';
  if (mlsInfo.daysOnMarket !== undefined && mlsInfo.daysOnMarket <= 3) {
    status = 'NEW';
    statusColor = '#2E7D32';
  } else if (mlsInfo.status?.toLowerCase() === 'pending') {
    status = 'PENDING';
    statusColor = '#E65100';
  } else if (mlsInfo.status?.toLowerCase() === 'active') {
    status = 'ACTIVE';
    statusColor = '#1565C0';
  }

  const bathsFull = property.bathsFull || 0;
  const bathsHalf = property.bathsHalf || 0;
  const bathsThreeQuarter = property.bathsThreeQuarter || 0;
  const totalBaths = bathsFull + (bathsHalf * 0.5) + (bathsThreeQuarter * 0.75);

  return {
    id: String(mls.mlsId || mls.listingId || ''),
    mlsId: mls.mlsId,
    price: mls.listPrice || 0,
    address: address.full ||
      [address.streetNumber, address.streetName, address.streetSuffix].filter(Boolean).join(' ') ||
      '',
    city: address.city || '',
    state: address.state || '',
    zipCode: address.postalCode || '',
    beds: property.bedrooms || 0,
    baths: Math.round(totalBaths * 10) / 10,
    sqft: property.area || 0,
    status,
    statusColor,
    primaryPhoto: (mls.photos && mls.photos[0]) || '',
  };
};

// Parses a Ria chat API response's structured_data into an array of property
// cards, or null when the response carries no property results. Mirrors the
// mobile app's ria.service.ts structured_data handling.
const parseRiaProperties = (structuredData) => {
  if (!structuredData) return null;
  try {
    if (structuredData.type === 'property_list' && Array.isArray(structuredData.data?.properties)) {
      const props = structuredData.data.properties.map(transformRiaMlsProperty).filter(Boolean);
      return props.length > 0 ? props : null;
    }
    if (structuredData.type === 'property_detail' && structuredData.data) {
      const prop = transformRiaMlsProperty(structuredData.data);
      return prop ? [prop] : null;
    }
  } catch (err) {
    console.warn('[Ria] Failed to parse structured_data properties:', err);
  }
  return null;
};

const DashboardPage = () => {
  const navigate = useNavigate();
  // Dev-only bypass for the DocuSign readiness gate below — stg/prod keep the full gate
  // exactly as it was; dev skips it (matches ConnectedServicesPage's isDevEnvironment gate).
  const isDevEnvironment = process.env.REACT_APP_FIREBASE_PROJECT_ID === 'rialty-ai-dev';
  const [userName, setUserName] = useState('User');
  const [userRole, setUserRole] = useState(getActiveRoleFromStorage); // Track user role/type

  // Left-align the greeting under the "Home" nav tab (DashboardHeader.js) instead of
  // centering it — the nav-tabs pill itself is centered on the page (see
  // DashboardHeader.css .dashboard-exact-nav-content), so "Home"'s left edge shifts with
  // viewport width and can't be matched with a fixed CSS offset. We measure it directly
  // and mirror it as a margin on the greeting. On tablet/mobile (≤1024px) the nav-tabs pill
  // is hidden behind the hamburger menu, so there's nothing to align to — greeting falls
  // back to its default left-aligned position (0 offset) there.
  const greetingRef = useRef(null);
  // The Ria chat container (search card). The per-task "Ask Ria" button
  // scrolls this into view before firing the prompt, so the user actually
  // sees Ria's reply land instead of silently appearing above the fold.
  const riaSectionRef = useRef(null);
  const [greetingOffset, setGreetingOffset] = useState(0);

  useEffect(() => {
    const alignGreetingWithHomeTab = () => {
      const tabsPill = document.querySelector('.dashboard-exact-nav-tabs');
      const greetingEl = greetingRef.current;
      if (!greetingEl) return;
      if (!tabsPill || tabsPill.offsetParent === null) {
        setGreetingOffset(0);
        return;
      }
      const homeLeft = tabsPill.getBoundingClientRect().left;
      const parentEl = greetingEl.parentElement;
      const parentPaddingLeft = parseFloat(getComputedStyle(parentEl).paddingLeft) || 0;
      // Natural (zero-margin) left edge of the greeting, i.e. the parent's content
      // box start — not the parent's own border-box edge, which ignores its padding.
      const greetingNaturalLeft = parentEl.getBoundingClientRect().left + parentPaddingLeft;
      setGreetingOffset(Math.max(0, homeLeft - greetingNaturalLeft));
    };

    alignGreetingWithHomeTab();
    window.addEventListener('resize', alignGreetingWithHomeTab);

    // Catches width changes that don't come from a viewport resize — e.g. the
    // Conversations unread-count badge appearing/disappearing after its own async fetch.
    const tabsPill = document.querySelector('.dashboard-exact-nav-tabs');
    const resizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(alignGreetingWithHomeTab) : null;
    if (resizeObserver && tabsPill) resizeObserver.observe(tabsPill);

    return () => {
      window.removeEventListener('resize', alignGreetingWithHomeTab);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, []);

  // Fast-path redirect for roles with dedicated dashboards.
  // Fires immediately on mount using whatever is already in localStorage,
  // so users who have currentRole already set (e.g. returning sessions) are
  // redirected without waiting for fetchUserData to complete.
  useEffect(() => {
    const role = getActiveRoleFromStorage();
    if (!role) return;
    if (role.toUpperCase() === 'SERVICE_PROVIDER') {
      navigate('/dashboard/sp', { replace: true });
    }
  }, [navigate]);
  const [userInfo, setUserInfo] = useState(null); // Store full user info
  const [hasTaskData, setHasTaskData] = useState(false); // Track if user has task data
  const [showAllTasks, setShowAllTasks] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [transactions, setTransactions] = useState([]); // Store user transactions
  const [isLoadingTransactions, setIsLoadingTransactions] = useState(false);
  // ProfileStatus payload from /api/v1/dashboard — drives the "Complete Your
  // Profile" card. Backend calculates completion % + checklist per role
  // (photo + personal info + license + brokerage for AGENT/TC/BROKER). Null
  // while loading; card renders only when `isComplete === false`.
  const [profileStatus, setProfileStatus] = useState(null);
  // Compact profile-completion card has a Dismiss (X) button that hides the
  // card for 7 days. We persist "hide-until" as an ISO timestamp in
  // localStorage so the dismiss survives reloads and tab switches, but
  // re-shows automatically on day 8. Hidden permanently once pct hits 100 —
  // that gate lives in renderProfileCompletionCard off profileStatus.
  const PROFILE_CARD_DISMISS_KEY = 'dashboard-profile-card-hide-until';
  const [profileCardDismissedUntil, setProfileCardDismissedUntil] = useState(() => {
    try { return localStorage.getItem(PROFILE_CARD_DISMISS_KEY) || null; }
    catch { return null; }
  });
  const [upcomingEvents, setUpcomingEvents] = useState([]); // Calendar-service upcoming events
  const [upcomingTxnNames, setUpcomingTxnNames] = useState({}); // transactionId -> name, for event/SR rows (picker API)
  const [upcomingFetchDone, setUpcomingFetchDone] = useState(false); // events + picker fetches settled — gate the Upcoming section on this
  const [upcomingVisibleCount, setUpcomingVisibleCount] = useState(5); // Upcoming rows shown; "Show More" reveals 5 more at a time
  // Which bucket of Upcoming Tasks the user is viewing: OVERDUE (dates before
  // today), WEEK (today / tomorrow — the window the dashboard currently fetches),
  // or ALL (both). Default is set once the data arrives: Overdue if any,
  // otherwise This week. Mirrors the mockup's tab shape.
  const [tasksFilter, setTasksFilter] = useState('ALL');
  const tasksFilterDefaultedRef = useRef(false);
  // Welcome popup state (for transaction invite flow)
  const [showWelcomePopup, setShowWelcomePopup] = useState(false);
  const [welcomeData, setWelcomeData] = useState(null);
  // DocuSign connect state
  const [isConnectingDocusign, setIsConnectingDocusign] = useState(false);
  const [isDisconnectingDocusign, setIsDisconnectingDocusign] = useState(false);
  const [docusignConnected, setDocusignConnected] = useState(null); // null=unknown, true=connected, false=not connected
  // DocuSign status card (banner) state
  const [dsCardLoading, setDsCardLoading] = useState(true);
  const [dsInviteStatus, setDsInviteStatus] = useState(null); // 'pending' | 'active' | null
  const [dsFormCount, setDsFormCount] = useState(null); // number | null
  // DocuSign pre-create-transaction readiness modal — always has a "Continue without DocuSign" escape hatch
  const [dsModal, setDsModal] = useState(null); // 'not_connected' | 'pending' | 'no_forms' | null
  const [dsModalDestination, setDsModalDestination] = useState(null); // route to navigate to once resolved
  const [dsModalRoomsUserId, setDsModalRoomsUserId] = useState(null); // for Resend Invite
  const [dsResendingInvite, setDsResendingInvite] = useState(false);
  const [dsResendSuccess, setDsResendSuccess] = useState(false);
  const [dsAddingFormProvider, setDsAddingFormProvider] = useState(false);
  const [dsChecking, setDsChecking] = useState(null); // destination currently being checked, or null
  // Ria AI chat state
  const [riaMessages, setRiaMessages] = useState([]);
  const [riaLoading, setRiaLoading] = useState(false);
  // True while the initial "most recent conversation" fetch (loadRiaHistory, on mount) is in
  // flight — RialtyAIService can take a long time to respond under concurrent load, and with
  // no indicator the widget just sits empty and looks broken for that whole window.
  const [riaLoadingHistory, setRiaLoadingHistory] = useState(true);
  const [riaConversationId, setRiaConversationId] = useState(null);
  const [isRiaChatExpanded, setIsRiaChatExpanded] = useState(false);
  const riaMessagesEndRef = useRef(null);
  const riaChatAreaRef = useRef(null);
  // Older-history pagination (scroll-to-top loads the previous conversation, Teams-style).
  // Refs, not state, since none of this needs a re-render on its own — only the
  // messages/loading-more/reached-start state below do.
  const riaSeenConvIdsRef = useRef(new Set());
  const riaOldestDateRef = useRef(null); // 'YYYY-MM-DD' cursor for the next end_date fetch
  const riaPendingQueueRef = useRef([]); // fetched-but-not-yet-displayed conversation summaries
  const riaHasMoreHistoryRef = useRef(true);
  const riaTopSentinelRef = useRef(null);
  // Background prefetch: once a conversation is queued, fetch its full message detail
  // ahead of time so it's just an insert (no network wait) when the user actually
  // scrolls to it — display timing is unchanged, still gated by the sentinel below.
  const riaDetailCacheRef = useRef(new Map()); // conversation id -> full conversation detail
  const riaPrefetchingIdRef = useRef(null); // id currently being prefetched, to avoid duplicate fetches
  const [riaLoadingOlder, setRiaLoadingOlder] = useState(false);
  const [riaReachedHistoryStart, setRiaReachedHistoryStart] = useState(false);
  // Date pill only shows while actively scrolling (Google/Apple Photos pattern) —
  // fades in on scroll, fades back out ~1s after scrolling stops, so it never
  // sits persistently over static content.
  const [riaDatePillVisible, setRiaDatePillVisible] = useState(false);
  const riaScrollStopTimeoutRef = useRef(null);
  // Exactly one date pill is ever shown (Teams/WhatsApp), computed from whichever
  // day-group is currently at the top of the viewport — NOT one sticky header per
  // day-group, which lets two pills appear stuck/visible at once when a day-group
  // is short enough that the next one is already on-screen too.
  const [riaActiveDateLabel, setRiaActiveDateLabel] = useState('');
  const riaGroupsRef = useRef([]); // latest groupRiaMessagesByDay(riaMessages) result, set at render time
  const riaDayGroupNodesRef = useRef(new Map()); // dateKey -> DOM node, for scroll-position lookups
  const updateRiaActiveDateLabel = () => {
    const chatEl = riaChatAreaRef.current;
    const groups = riaGroupsRef.current;
    if (!chatEl || groups.length === 0) return;
    const containerTop = chatEl.getBoundingClientRect().top;
    let active = groups[0];
    for (const group of groups) {
      const node = riaDayGroupNodesRef.current.get(group.dateKey);
      if (!node) continue;
      const nodeTop = node.getBoundingClientRect().top - containerTop + chatEl.scrollTop;
      if (nodeTop <= chatEl.scrollTop + 8) {
        active = group;
      } else {
        break;
      }
    }
    setRiaActiveDateLabel(active.label);
  };
  const handleRiaChatAreaScroll = () => {
    setRiaDatePillVisible(true);
    updateRiaActiveDateLabel();
    if (riaScrollStopTimeoutRef.current) clearTimeout(riaScrollStopTimeoutRef.current);
    riaScrollStopTimeoutRef.current = setTimeout(() => setRiaDatePillVisible(false), 1000);
  };
  useEffect(() => () => {
    if (riaScrollStopTimeoutRef.current) clearTimeout(riaScrollStopTimeoutRef.current);
  }, []);

  // Helper function to check if user has a specific role
  const hasRole = (roles, roleName) => {
    if (!roles) return false;
    if (Array.isArray(roles)) {
      return roles.some(r => r.toUpperCase() === roleName.toUpperCase());
    }
    return roles.toUpperCase() === roleName.toUpperCase();
  };

  // Helper function to check if user is an agent or transaction coordinator
  const isAgent = (roles) => hasRole(roles, 'AGENT') || hasRole(roles, 'TRANSACTION_COORDINATOR') || hasRole(roles, 'BROKER');

  // Function to fetch dashboard data
  const fetchDashboardData = async (userData = null) => {
    setIsLoadingTransactions(true);

    try {
      // Wait for Firebase auth to be ready and get fresh token
      const getFirebaseToken = () => {
        return new Promise((resolve, reject) => {
          // If user is already available, get token directly
          if (auth.currentUser) {
            auth.currentUser.getIdToken(true).then(resolve).catch(reject);
            return;
          }
          // Otherwise wait for auth state to be ready (with timeout)
          const timeoutId = setTimeout(() => {
            unsubscribe();
            reject(new Error('Firebase auth timeout'));
          }, 10000);
          const unsubscribe = auth.onAuthStateChanged(async (user) => {
            clearTimeout(timeoutId);
            unsubscribe();
            if (user) {
              try {
                const token = await user.getIdToken(true);
                resolve(token);
              } catch (error) {
                reject(error);
              }
            } else {
              reject(new Error('No authenticated user'));
            }
          });
        });
      };

      const firebaseToken = await getFirebaseToken();

      const viewingRole = getActiveRole(userData);
      const dashboardUrl = viewingRole
        ? `${API_ENDPOINTS.dashboard}?viewingRole=${viewingRole}`
        : API_ENDPOINTS.dashboard;

      const response = await apiClient.get(
        dashboardUrl,
        {
          headers: {
            'Authorization': `Bearer ${firebaseToken}`,
            'Content-Type': 'application/json'
          },
          // Dashboard aggregation makes ~50 sequential Firestore queries server-side;
          // against a local backend (high Firestore latency) it can take ~30s, which
          // is right at the apiClient default. Give it room instead of aborting.
          timeout: 90000
        }
      );

      console.log('Dashboard data fetched:', response.data);
      // Handle different response formats (array, transactionCards, transactions, content, data)
      const data = response.data;
      let transactionData = [];
      if (Array.isArray(data)) {
        transactionData = data;
      } else if (data?.transactionCards && Array.isArray(data.transactionCards)) {
        transactionData = data.transactionCards;
      } else if (data?.transactions && Array.isArray(data.transactions)) {
        transactionData = data.transactions;
      } else if (data?.content && Array.isArray(data.content)) {
        transactionData = data.content;
      } else if (data?.data && Array.isArray(data.data)) {
        transactionData = data.data;
      }
      setTransactions(transactionData);
      setHasTaskData(transactionData.length > 0);
      // ProfileStatus is only present when the aggregator returned a shaped
      // DashboardResponse object (not a bare array). Ignore otherwise.
      if (data && !Array.isArray(data) && data.profileStatus) {
        setProfileStatus(data.profileStatus);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      setTransactions([]);
    } finally {
      setIsLoadingTransactions(false);
    }
  };

  // Force scroll to top on every dashboard load — disable browser scroll restoration first
  // so it cannot override the position after the component mounts
  useEffect(() => {
    if (window.history.scrollRestoration) {
      window.history.scrollRestoration = 'manual';
    }
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, []);

  // Load the user's upcoming calendar events (calendar_events) for the dashboard.
  // Separate from Upcoming Tasks (milestones) — these are scheduled appointments.
  // Also loads a slim transaction picker list to resolve transaction names for
  // event/SR rows — dashboard cards only cover up to 10 non-prospect transactions.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const firebaseToken = localStorage.getItem('firebaseToken');
      if (!firebaseToken) return; // not logged in yet — dashboard fetch handles redirect
      const authHeaders = { headers: { 'Authorization': `Bearer ${firebaseToken}` } };
      try {
        const res = await apiClient.get(`${API_BASE_URL}/api/v1/calendar/events/upcoming`, authHeaders);
        const arr = Array.isArray(res.data?.data) ? res.data.data : [];
        if (!cancelled) setUpcomingEvents(arr);
      } catch (err) {
        if (!cancelled) console.error('Error fetching upcoming events:', err);
      }
      try {
        const pickerRes = await apiClient.get(`${API_BASE_URL}/api/v1/transactions/picker?size=100`, authHeaders);
        const content = pickerRes.data?.content || pickerRes.data?.data?.content || [];
        const map = {};
        content.forEach((t) => { map[String(t.id)] = t.transactionName || t.propertyAddress || ''; });
        if (!cancelled) setUpcomingTxnNames(map);
      } catch (err) {
        if (!cancelled) console.error('Error fetching transaction names for upcoming rows:', err);
      }
      // Both fetches settled (success or failure) — the Upcoming section can render
      if (!cancelled) setUpcomingFetchDone(true);
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    // Set on unmount. Only the Service Provider redirect below checks it: when
    // this effect has to await /users/me first, the user may already have left
    // /dashboard/sp for another page, and a late redirect would bounce them back.
    let cancelled = false;
    const fetchUserData = async () => {
      const userData = localStorage.getItem('user');
      const isAuthenticated = localStorage.getItem('isAuthenticated');
      const firebaseToken = localStorage.getItem('firebaseToken');
      console.log("==userData==",userData);
      // Redirect to home page if no user session exists
      if (!userData || !isAuthenticated) {
        navigate('/');
        return;
      }

      if (userData) {
        try {
          let user = JSON.parse(userData);
          console.log("==user==",user);

          // Only call /users/me if localStorage is missing essential data (name, roles, or id)
          // Once localStorage has complete data (after login or first /users/me call), this won't run again
          const hasName = user.firstName || user.fullName || user.name;
          const hasRoles = user.roles || user.role || user.userType;
          const hasId = user.id || user.odooId;
          if (!hasName || !hasRoles || !hasId) {
            console.log('User data incomplete in localStorage, fetching from /users/me (one-time)...');
            try {
              const getToken = () => {
                return new Promise((resolve, reject) => {
                  const unsubscribe = auth.onAuthStateChanged(async (fbUser) => {
                    unsubscribe();
                    if (fbUser) {
                      try {
                        const token = await fbUser.getIdToken(true);
                        resolve(token);
                      } catch (err) { reject(err); }
                    } else { reject(new Error('No authenticated user')); }
                  });
                });
              };
              const token = await getToken();
              const meResponse = await apiClient.get(API_ENDPOINTS.userMe, {
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                },
                timeout: 10000
              });
              // Handle nested response formats
              let meData = meResponse.data;
              if (meData?.data && typeof meData.data === 'object' && !Array.isArray(meData.data)) {
                meData = meData.data;
              } else if (meData?.content && typeof meData.content === 'object' && !Array.isArray(meData.content)) {
                meData = meData.content;
              }
              if (meData && typeof meData === 'object') {
                user = { ...user, ...meData };
                localStorage.setItem('user', JSON.stringify(user));
                console.log('User data updated from /users/me and saved to localStorage:', user);
              }
            } catch (meError) {
              console.error('Failed to fetch user data from /users/me:', meError);
            }
          } else {
            console.log('User data already complete in localStorage, skipping /users/me call');
          }

          // Build full name from firstName and lastName, or fallback to email
          const firstName = user.firstName || '';
          const lastName = user.lastName || '';
          const fullName = `${firstName} ${lastName}`.trim() || user.fullName || user.name || user.email || 'User';
          setUserName(firstName || fullName.split(' ')[0]);

          // Determine active role — priority: currentRole (localStorage) > user.role > roles[0]
          const activeRole = getActiveRole(user);
          // Always persist so DashboardHeader reads the correct value, including BROKERAGE
          localStorage.setItem('currentRole', activeRole);
          setUserRole(activeRole);
          console.log("Active role:", activeRole);

          // Redirect role-specific users to their dedicated dashboards.
          // Done here (after user data is resolved) so we catch cases where
          // LoginPage didn't write currentRole (e.g. backend returned no activeRole for SERVICE_PROVIDER).
          if (activeRole.toUpperCase() === 'SERVICE_PROVIDER') {
            if (cancelled) return;
            navigate('/dashboard/sp', { replace: true });
            return;
          }

          // Get Firebase UID
          const firebaseUid = user.firebaseUid || user.uid || '';
          console.log("Firebase UID:", firebaseUid);

          // Fetch dashboard data for all users
          fetchDashboardData(user);

          // Full 3-step DocuSign status check — Agent and TC (TC has DocuSign parity with Agent)
          const userRoles = user.roles || user.role || user.userType || '';
          const userIsAgentRole = Array.isArray(userRoles)
            ? userRoles.some(r => r?.toUpperCase() === 'AGENT' || r?.toUpperCase() === 'TRANSACTION_COORDINATOR')
            : (userRoles?.toUpperCase() === 'AGENT' || userRoles?.toUpperCase() === 'TRANSACTION_COORDINATOR');
          // CA is excluded from this account-status check (banner stays hidden for CA agents).
          // Document/envelope/form functionality elsewhere is untouched and unaffected by this.
          // DocuSign is COLORADO-ONLY — this banner must only show for CO agents. Was `!== 'CA'`,
          // which wrongly surfaced it for TX (and every non-CO, non-CA state).
          const userLicenseState = (user.licenseState || user.stateCode || user.state || '').toUpperCase();
          if (userIsAgentRole && firebaseToken && (userLicenseState === 'CO' || userLicenseState === '')) {
            setDsCardLoading(true);
            try {
              // Step 1: connection
              const dsResponse = await apiClient.get(API_ENDPOINTS.docusignConnect, {
                headers: { 'Authorization': `Bearer ${firebaseToken}` }
              });
              const connected = dsResponse.data?.connected === true;
              setDocusignConnected(connected);

              if (connected && dsResponse.data?.email) {
                const dsEmail = dsResponse.data.email;
                // Step 2: invite status
                try {
                  const usersResp = await apiClient.get(API_ENDPOINTS.docusignRoomsUsers, {
                    headers: { 'Authorization': `Bearer ${firebaseToken}` }
                  });
                  const users = usersResp.data?.userSummaries || [];
                  const self = users.find(u => u.email?.toLowerCase() === dsEmail.toLowerCase());
                  if (self) {
                    const invStatus = (self.status || '').toLowerCase();
                    setDsInviteStatus(invStatus);
                    if (invStatus === 'active') {
                      // Step 3: form libraries
                      try {
                        const userState = user.licenseState || user.state || 'CA';
                        const formsResp = await apiClient.get(API_ENDPOINTS.docusignFormLibraries, {
                          headers: { 'Authorization': `Bearer ${firebaseToken}` },
                          params: { state: userState }
                        });
                        const data = formsResp.data;
                        let libs = [];
                        if (Array.isArray(data)) libs = data;
                        else if (data?.formLibraries && Array.isArray(data.formLibraries)) libs = data.formLibraries;
                        else if (data?.formsLibrarySummaries && Array.isArray(data.formsLibrarySummaries)) libs = data.formsLibrarySummaries;
                        setDsFormCount(libs.length);
                      } catch {
                        setDsFormCount(null);
                      }
                    }
                  }
                } catch {
                  // silently allow
                }
              }
            } catch (e) {
              console.error('DocuSign status check failed:', e);
            } finally {
              setDsCardLoading(false);
            }
          } else {
            setDsCardLoading(false);
          }

          // If no profile photo in user data, fetch from API
          if (!user.profilePhotoBase64 && !user.profilePhotoUrl && user.id && firebaseToken) {
            try {
              const response = await apiClient.get(API_ENDPOINTS.userPhoto(user.id), {
                headers: {
                  'Authorization': `Bearer ${firebaseToken}`
                },
                responseType: 'blob'
              });

              const contentType = response.headers['content-type'] || '';
              let profilePhoto = '';

              // Check if response is binary image or JSON
              if (contentType.includes('image')) {
                // Handle binary image data - convert to base64
                profilePhoto = await new Promise((resolve) => {
                  const reader = new FileReader();
                  reader.onloadend = () => resolve(reader.result);
                  reader.readAsDataURL(response.data);
                });
              } else {
                // Handle JSON response - parse the blob as text then JSON
                const text = await response.data.text();
                const photoData = JSON.parse(text);
                profilePhoto = photoData.profilePhotoBase64 || photoData.photoBase64 || photoData.base64 || '';
              }

              // Update user data with fetched photo
              if (profilePhoto) {
                user.profilePhotoBase64 = profilePhoto;
                localStorage.setItem('user', JSON.stringify(user));
                console.log('Profile photo fetched and cached in Dashboard');
              }
            } catch (photoError) {
              console.error('Failed to fetch profile photo:', photoError);
            }
          }

          // Store full user info for display (after photo fetch)
          setUserInfo(user);

          // Check for welcome transaction data (from invite registration flow)
          const welcomeTransactionData = sessionStorage.getItem('welcomeTransactionData');
          if (welcomeTransactionData) {
            // Remove immediately so it never shows again on re-visit
            sessionStorage.removeItem('welcomeTransactionData');
            try {
              const parsedWelcome = JSON.parse(welcomeTransactionData);
              setWelcomeData(parsedWelcome);
              setShowWelcomePopup(true);
            } catch (err) {
              console.error('Error parsing welcome transaction data:', err);
            }
          }

        } catch (error) {
          console.error('Error parsing user data:', error);
          // Redirect to home page if user data is corrupted
          navigate('/');
        }
      }
    };

    fetchUserData();
    return () => { cancelled = true; };
  }, [navigate]);

  // Shared Firebase token getter for all Ria calls (chat send, history load, older-history load).
  const getRiaFirebaseToken = () => {
    return new Promise((resolve, reject) => {
      if (auth.currentUser) {
        auth.currentUser.getIdToken(true).then(resolve).catch(reject);
        return;
      }
      const timeoutId = setTimeout(() => {
        unsubscribe();
        reject(new Error('Firebase auth timeout'));
      }, 10000);
      const unsubscribe = auth.onAuthStateChanged(async (user) => {
        clearTimeout(timeoutId);
        unsubscribe();
        if (user) {
          try {
            const token = await user.getIdToken(true);
            resolve(token);
          } catch (error) {
            reject(error);
          }
        } else {
          reject(new Error('No authenticated user'));
        }
      });
    });
  };

  // Normalizes a backend conversation's messages into the shape the chat UI renders,
  // keeping a real Date (createdAt) alongside the pre-formatted time string so
  // messages can be grouped into day buckets for the sticky date headers.
  const normalizeRiaMessages = (conv) =>
    (conv.messages || []).map((msg) => ({
      role: msg.role === 'user' ? 'user' : 'ria',
      content: msg.content,
      createdAt: msg.created_at ? new Date(msg.created_at) : null,
      time: msg.created_at ? formatRiaMessageTimestamp(new Date(msg.created_at)) : '',
      properties: msg.role === 'user' ? undefined : (parseRiaProperties(msg.structured_data) || undefined),
      noteLinks: msg.role === 'user' ? undefined : extractRiaNoteLinks(msg),
      cmaReportLink: msg.role === 'user' ? undefined : extractCmaReportLink(msg),
    }));

  // Fetches the next batch of older conversation summaries using the end_date cursor.
  // end_date is date-granular (not a timestamp), so a batch can re-include conversations
  // already seen on the cursor's boundary day — filter those out, and if an entire batch
  // turns out to be duplicates, step the cursor one day earlier and retry, so days with
  // many same-day conversations (seen with the ashurialtytest01 test account) don't get
  // skipped or looped on forever.
  const fetchNextRiaConversationBatch = async (firebaseToken) => {
    if (!riaHasMoreHistoryRef.current) return [];
    let cursorDate = riaOldestDateRef.current;
    for (let attempt = 0; attempt < 30; attempt++) {
      const params = { limit: 20 };
      if (cursorDate) params.end_date = cursorDate;
      const resp = await apiClient.get(API_ENDPOINTS.riaConversations, {
        headers: { Authorization: `Bearer ${firebaseToken}` },
        params,
      });
      const batch = resp.data?.conversations || [];
      if (batch.length === 0) {
        riaHasMoreHistoryRef.current = false;
        return [];
      }
      const fresh = batch.filter((c) => !riaSeenConvIdsRef.current.has(c.id));
      const oldestInBatch = batch[batch.length - 1]; // ordered desc by updated_at
      const oldestDateStr = oldestInBatch?.updated_at?.slice(0, 10);
      if (fresh.length > 0) {
        fresh.forEach((c) => riaSeenConvIdsRef.current.add(c.id));
        if (oldestDateStr) riaOldestDateRef.current = oldestDateStr;
        return fresh;
      }
      if (!oldestDateStr) {
        riaHasMoreHistoryRef.current = false;
        return [];
      }
      const prevDay = new Date(`${oldestDateStr}T00:00:00Z`);
      prevDay.setUTCDate(prevDay.getUTCDate() - 1);
      cursorDate = prevDay.toISOString().slice(0, 10);
      riaOldestDateRef.current = cursorDate;
    }
    return [];
  };

  // Fetches one queued conversation's full message detail in the background and
  // caches it, so the reveal step below can skip the network wait entirely when
  // the user actually scrolls to it. Fire-and-forget: failures just mean the
  // reveal step falls back to fetching it live, same as before this existed.
  const prefetchNextQueuedRiaConversation = async (firebaseToken) => {
    const upcoming = riaPendingQueueRef.current[0];
    if (!upcoming) return;
    if (riaDetailCacheRef.current.has(upcoming.id)) return;
    if (riaPrefetchingIdRef.current === upcoming.id) return;
    riaPrefetchingIdRef.current = upcoming.id;
    try {
      const detailResp = await apiClient.get(API_ENDPOINTS.riaConversationById(upcoming.id), {
        headers: { Authorization: `Bearer ${firebaseToken}` },
      });
      const conv = detailResp.data?.conversation;
      if (conv) riaDetailCacheRef.current.set(upcoming.id, conv);
    } catch (error) {
      console.warn('[RIA] Background prefetch failed (will fetch live on reveal):', error?.message);
    } finally {
      if (riaPrefetchingIdRef.current === upcoming.id) riaPrefetchingIdRef.current = null;
    }
  };

  // Loads the next older conversation (from the pending queue, fetching a fresh batch
  // from the server when the queue runs dry) and prepends its messages, preserving
  // scroll position so the view doesn't jump — the scroll-up-to-load-more mechanic.
  const riaLoadingOlderRef = useRef(false); // synchronous re-entrancy lock — riaLoadingOlder
  // (React state) only updates on the next render, so a second scroll/observer callback
  // firing in the same tick would otherwise see the stale "false" and race past the guard.
  const loadOlderRiaConversation = async () => {
    if (riaLoadingOlderRef.current || riaReachedHistoryStart) return;
    riaLoadingOlderRef.current = true;
    setRiaLoadingOlder(true);
    // Defensively clear the scroll-to-bottom flag: it's only ever set by the
    // send/receive/initial-load paths, but if one of those is still mid-flight
    // (its own scroll-to-bottom effect hasn't committed yet) when this prepend's
    // setRiaMessages also lands, both share the same [riaMessages] effect below —
    // and it would jump back to the bottom right after the prepend restores position.
    riaShouldScrollToBottomRef.current = false;
    try {
      const firebaseToken = await getRiaFirebaseToken();
      if (riaPendingQueueRef.current.length === 0) {
        const fresh = await fetchNextRiaConversationBatch(firebaseToken);
        riaPendingQueueRef.current.push(...fresh);
      }
      if (riaPendingQueueRef.current.length === 0) {
        setRiaReachedHistoryStart(true);
        return;
      }
      const next = riaPendingQueueRef.current.shift();
      // Reveal timing is unchanged — still only happens here, on the scroll trigger.
      // Only the data-fetch is decoupled: use the cached detail if the background
      // prefetch already finished it, otherwise fetch live (first scroll before any
      // prefetch has had time to complete).
      const cached = riaDetailCacheRef.current.get(next.id);
      let conv = cached;
      if (cached) {
        riaDetailCacheRef.current.delete(next.id);
      } else {
        const detailResp = await apiClient.get(API_ENDPOINTS.riaConversationById(next.id), {
          headers: { Authorization: `Bearer ${firebaseToken}` },
        });
        conv = detailResp.data?.conversation;
      }
      if (!conv) return;
      const normalized = normalizeRiaMessages(conv);

      const chatEl = riaChatAreaRef.current;
      const prevScrollHeight = chatEl ? chatEl.scrollHeight : 0;
      setRiaMessages((prev) => [...normalized, ...prev]);
      requestAnimationFrame(() => {
        if (!chatEl) return;
        chatEl.scrollTop = chatEl.scrollHeight - prevScrollHeight;
        // The IntersectionObserver below only fires on a transition into the lookahead
        // zone, not just for sitting inside it — so if the conversation we just loaded
        // was shorter than that zone, the restored position never actually leaves it,
        // and the observer would otherwise never re-fire on its own even though more
        // history exists. Explicitly continue the chain in that case.
        if (chatEl.scrollTop <= RIA_HISTORY_LOOKAHEAD_PX) {
          loadOlderRiaConversation();
        }
      });
      // Kick off background prefetch for the new front of the queue (fire-and-forget)
      // so the *next* scroll-triggered reveal is instant too.
      prefetchNextQueuedRiaConversation(firebaseToken);
    } catch (error) {
      console.warn('[RIA] Failed to load older conversation:', error?.message);
    } finally {
      riaLoadingOlderRef.current = false;
      setRiaLoadingOlder(false);
    }
  };

  // Keep the latest loadOlderRiaConversation in a ref so the IntersectionObserver
  // effect below can stay mounted once without re-subscribing on every state change.
  const loadOlderRiaConversationRef = useRef(loadOlderRiaConversation);
  loadOlderRiaConversationRef.current = loadOlderRiaConversation;

  // Scroll-to-top trigger: an IntersectionObserver on a sentinel at the top of the
  // chat area, rather than a scrollTop/scrollHeight check — the latter never fires
  // when the current content is too short to overflow the container (confirmed as
  // the reason the equivalent mechanism silently did nothing on the disabled
  // ConversationPage Weekly/Monthly tabs). rootMargin starts the fetch ~60px before
  // the sentinel reaches the true top edge, so history is already in place by the
  // time the user gets there — this also keeps the user from ever sitting exactly
  // at the scrollTop=0 edge, where a slightly-imprecise restore could leave the
  // sentinel re-intersecting. Kept small (not the mobile app's 100-200px) because
  // this panel's scroll area is only ~200-400px tall — a bigger margin would fire
  // while most of the panel is still unscrolled, well before the user is near the top.
  useEffect(() => {
    const sentinel = riaTopSentinelRef.current;
    const root = riaChatAreaRef.current;
    if (!sentinel || !root) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && riaInitialScrollDoneRef.current) {
          loadOlderRiaConversationRef.current();
        }
      },
      { root, threshold: 0, rootMargin: `${RIA_HISTORY_LOOKAHEAD_PX}px 0px 0px 0px` }
    );
    observer.observe(sentinel);

    // Backup trigger: IntersectionObserver only fires on a boundary CROSSING,
    // not while the sentinel sits still inside the zone — so if a fast upward
    // scroll lands at the top in the same tick the observer's crossing gets
    // reported (or the crossing fires while a load is still gated on
    // riaInitialScrollDoneRef.current), nothing re-fires afterward and the
    // user is stuck at the top with no more history loading, until they
    // scroll away and back in to generate a fresh crossing. A plain scroll
    // listener checking position directly closes that gap; loadOlderRiaConversation
    // already no-ops via its own reentrancy/history-exhausted guards, so
    // calling it redundantly here is safe.
    const handleScroll = () => {
      if (riaInitialScrollDoneRef.current && root.scrollTop <= RIA_HISTORY_LOOKAHEAD_PX) {
        loadOlderRiaConversationRef.current();
      }
    };
    root.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      observer.disconnect();
      root.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Handle Ask Ria - send message to Ria AI.
  // `overrideMessage` lets per-task "Ask Ria" buttons compose a contextual
  // prompt (e.g. "Help me with Draft CBS Contract — Test Transaction") and
  // bypass the input bar entirely. When omitted, this reads searchQuery as
  // before — the input-bar's own path is unchanged.
  const handleAskRia = async (overrideMessage) => {
    const message = typeof overrideMessage === 'string'
      ? overrideMessage.trim()
      : searchQuery.trim();
    if (!message || riaLoading) return;

    // Add user message to chat with timestamp
    const now = new Date();
    const timeStr = formatRiaMessageTimestamp(now);
    riaShouldScrollToBottomRef.current = true;
    setRiaMessages(prev => [...prev, { role: 'user', content: message, createdAt: now, time: timeStr }]);
    // Only clear the input bar when the message actually came from it — a
    // task-driven override must not wipe whatever the user had typed.
    if (typeof overrideMessage !== 'string') setSearchQuery('');
    setRiaLoading(true);

    try {
      const firebaseToken = await getRiaFirebaseToken();

      const requestBody = {
        message,
        conversation_id: riaConversationId || undefined,
        context: {}
      };

      const response = await apiClient.post(
        API_ENDPOINTS.riaChat,
        requestBody,
        {
          headers: {
            'Authorization': `Bearer ${firebaseToken}`,
            'Content-Type': 'application/json'
          },
          // Ria answers with tool calls take ~40s against the local backend
          // (fast on GKE); the 30s apiClient default aborts before they finish.
          // Raised past that for get_cma_report specifically — a real CMA
          // generation was observed taking 88s in stg, budgeted to 180s at the
          // gateway (rialty-api-gateway's analysisServiceCircuitBreaker).
          timeout: 200000
        }
      );

      console.log('Ria response:', response.data);

      // Update conversation_id if returned
      if (response.data?.conversation_id) {
        setRiaConversationId(response.data.conversation_id);
      }

      // Add Ria's response to chat with timestamp
      const riaReply = response.data?.message || response.data?.response || response.data?.content || response.data?.answer || 'No response from Ria.';
      const riaProperties = parseRiaProperties(response.data?.structured_data);
      const riaNow = new Date();
      const riaTime = formatRiaMessageTimestamp(riaNow);
      riaShouldScrollToBottomRef.current = true;
      const riaNoteLinks = extractRiaNoteLinks(response.data);
      const riaCmaReportLink = extractCmaReportLink(response.data);
      setRiaMessages(prev => [...prev, { role: 'ria', content: riaReply, createdAt: riaNow, time: riaTime, properties: riaProperties || undefined, noteLinks: riaNoteLinks, cmaReportLink: riaCmaReportLink }]);
    } catch (error) {
      console.error('Error asking Ria:', error);
      const errNow = new Date();
      const errTime = formatRiaMessageTimestamp(errNow);
      riaShouldScrollToBottomRef.current = true;
      setRiaMessages(prev => [...prev, { role: 'ria', content: 'Sorry, I couldn\'t process your request. Please try again.', createdAt: errNow, time: errTime }]);
    } finally {
      setRiaLoading(false);
    }
  };

  // Per-task "Ask Ria" entry point. Composes a contextual prompt from the
  // upcoming-task item ("Help me with {title} — {transactionName}"), scrolls
  // the Ria chat card into view so the user sees the reply land, then fires
  // handleAskRia with the composed message (bypassing searchQuery entirely).
  // Mirrors the mobile dashboard's RiaChatScreen contextual-prompt entry.
  const askRiaAboutTask = (item) => {
    if (!item) return;
    if (riaLoading) return;
    const title = (item.title || '').trim();
    if (!title) return;
    const txn = (item.transactionName || '').trim();
    const prompt = txn ? `Help me with ${title} — ${txn}` : `Help me with ${title}`;
    if (riaSectionRef.current && typeof riaSectionRef.current.scrollIntoView === 'function') {
      riaSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    handleAskRia(prompt);
  };

  // Auto-scroll Ria chat area to the newest message — but ONLY for genuine
  // "new message" events (initial load, send, reply). Set riaShouldScrollToBottomRef
  // to true right before a setRiaMessages call that should snap to the bottom;
  // leave it untouched (false) for history prepends, whose own scroll-position
  // restore logic (in loadOlderRiaConversation) must not be overridden here.
  const riaShouldScrollToBottomRef = useRef(false);
  // Guards the top-sentinel IntersectionObserver below: on mount, the sentinel sits
  // right at the (empty) chat area's top edge, so it reads as "intersecting" and
  // fires immediately — before the initial history load has even scrolled the pane
  // to the bottom. Left unguarded, that premature loadOlderRiaConversation() call
  // prepends an older conversation and re-anchors the scroll position to the
  // boundary between it and the latest conversation, leaving the pane looking
  // "stuck in the middle" instead of at the newest message. Flips true the first
  // time a real scroll-to-bottom actually runs, after which manual scroll-up-to-
  // load-more behaves exactly as before.
  const riaInitialScrollDoneRef = useRef(false);
  useEffect(() => {
    if (riaShouldScrollToBottomRef.current && riaChatAreaRef.current && riaMessages.length > 0) {
      riaChatAreaRef.current.scrollTop = riaChatAreaRef.current.scrollHeight;
      riaShouldScrollToBottomRef.current = false;
      riaInitialScrollDoneRef.current = true;
    }
  }, [riaMessages]);

  // Close the expanded Ria chat popup on Escape
  useEffect(() => {
    if (!isRiaChatExpanded) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setIsRiaChatExpanded(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isRiaChatExpanded]);

  // Load most recent Ria conversation on mount so prior chat history shows by default
  // (mirrors ConversationPage.handleRiaChatSelect / mobile RiaService.loadLatestConversation)
  useEffect(() => {
    const loadRiaHistory = async () => {
      try {
        const firebaseToken = await getRiaFirebaseToken();

        // Step 1: does the user have any prior Ria conversation at all?
        // duration=all: fetch the true latest conversation regardless of age,
        // not just one from the last 7 days (default filter is date-scoped).
        // retry: this fires once on mount with no user-visible fallback if it
        // fails, so a transient blip (gateway circuit-breaker fallback, backend
        // cold start, dropped connection) would otherwise silently leave the
        // chat looking empty for the whole session. Opt into the shared
        // network-level retry (3 attempts, default predicate = timeouts/no-response/502-504).
        const listResp = await apiClient.get(API_ENDPOINTS.riaConversations, {
          headers: { 'Authorization': `Bearer ${firebaseToken}` },
          params: { limit: 1, duration: 'all' },
          retry: { retries: 3, retryDelayMs: 800 }
        });
        const conversations = listResp.data?.conversations || [];
        if (conversations.length === 0) return;

        // Step 2: only fetch full history when a conversation actually has data
        const latestConv = conversations[0];
        const detailResp = await apiClient.get(API_ENDPOINTS.riaConversationById(latestConv.id), {
          headers: { 'Authorization': `Bearer ${firebaseToken}` },
          retry: { retries: 3, retryDelayMs: 800 }
        });
        const conv = detailResp.data?.conversation;
        if (!conv) return;

        const normalized = normalizeRiaMessages(conv);

        // Don't clobber messages the user already started sending while history was loading
        riaShouldScrollToBottomRef.current = true;
        setRiaMessages(prev => (prev.length === 0 ? normalized : prev));
        setRiaConversationId(prev => prev || conv.id);

        // Seed the older-history cursor so scrolling up fetches the NEXT
        // conversation back, not this same one again.
        riaSeenConvIdsRef.current.add(latestConv.id);
        if (latestConv.updated_at) {
          riaOldestDateRef.current = latestConv.updated_at.slice(0, 10);
        }
      } catch (error) {
        console.warn('Failed to load Ria conversation history:', error?.message);
      } finally {
        setRiaLoadingHistory(false);
      }
    };

    loadRiaHistory();
  }, []);

  // Check if current user is an agent (Broker and TC get the same access as Agent)
  const userIsAgent = userRole?.toUpperCase() === 'AGENT' || userRole?.toUpperCase() === 'TRANSACTION_COORDINATOR' || userRole?.toUpperCase() === 'BROKER';
  // Read fresh from localStorage rather than the `userInfo` state — userInfo populates only
  // after several sequential awaits in fetchUserData (token, /users/me, 3-step DocuSign check,
  // profile photo), so a render soon after page load could otherwise see userInfo still null
  // (same reliability issue documented in handleCreateTransactionClick below).
  const _dashboardAgentState = (() => {
    try {
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      return (u.licenseState || u.stateCode || u.state || '').toUpperCase();
    } catch { return ''; }
  })();
  // DocuSign features are visible to Agent, Broker, and TC roles — TC has full DocuSign parity with Agent.
  // CA is excluded: DocuSign account-status checks (banner, page-load check, pre-create readiness
  // gate) are not offered for CA agents. Document/envelope/form functionality elsewhere
  // (TransactionDocuments.js etc.) is untouched by this and keeps working for CA as before.
  // DocuSign is COLORADO-ONLY — every other state (CA, TX, …) uses Lone Wolf, so these DocuSign
  // account-status checks must only appear for CO agents. Was `!== 'CA'` (CA only), which wrongly
  // showed the DocuSign gate for TX (and any non-CO, non-CA state). `=== ''` fail-open keeps a CO
  // agent whose state briefly reads empty on load working.
  const canSeeDocuSign = (userRole?.toUpperCase() === 'AGENT' || userRole?.toUpperCase() === 'BROKER' || userRole?.toUpperCase() === 'TRANSACTION_COORDINATOR')
    && (_dashboardAgentState === 'CO' || _dashboardAgentState === '');

  // Derive which DocuSign banner state to show (mirrors mobile DashboardScreen logic)
  // Returns null when card should be hidden (all set up, or not an agent)
  const docuSignBanner = (() => {
    if (!canSeeDocuSign) return null;
    if (dsCardLoading) return 'loading';
    if (docusignConnected === null) return null;
    if (docusignConnected === false) return 'not_connected';
    if (dsInviteStatus === 'pending') return 'pending';
    if (dsInviteStatus === 'active' && dsFormCount === 0) return 'no_forms';
    return null;
  })();

  // Helper function to get status color
  const getStatusColor = (status) => {
    const statusColors = {
      'PROSPECT': '#F59E0B',
      'ACTIVE': '#4F46E5',
      'FELL_THROUGH': '#EF4444',
      'CLOSED': '#10B981',
      'PENDING': '#F59E0B',
      'UNDER_CONTRACT': '#4F46E5'
    };
    return statusColors[status?.toUpperCase()] || '#6B7280';
  };

  // Helper function to format status text
  const formatStatus = (status) => {
    const statusText = {
      'PROSPECT': 'Prospect',
      'ACTIVE': 'Active',
      'FELL_THROUGH': 'Fell Through',
      'CLOSED': 'Closed',
      'PENDING': 'Pending',
      'UNDER_CONTRACT': 'Under Contract'
    };
    return statusText[status?.toUpperCase()] || status || 'Unknown';
  };

  // Helper function to get urgency color
  const getUrgencyColor = (urgency) => {
    const urgencyColors = {
      'CRITICAL': '#EF4444',
      'WARNING': '#F59E0B',
      'NORMAL': '#10B981',
      'NONE': '#6B7280',
      'PENDING': '#F59E0B'
    };
    return urgencyColors[urgency?.toUpperCase()] || '#6B7280';
  };

  // Helper function to get urgency background color
  const getUrgencyBgColor = (urgency) => {
    const urgencyBgColors = {
      'CRITICAL': '#FEE2E2',
      'WARNING': '#FEF3C7',
      'NORMAL': '#D1FAE5',
      'NONE': '#F3F4F6',
      'PENDING': '#FEF3C7'
    };
    return urgencyBgColors[urgency?.toUpperCase()] || '#F3F4F6';
  };

  // Derive dot color — status text takes priority over urgency
  const getTaskDotColor = (urgency, status) => {
    const s = (status || '').toUpperCase();
    // 'Pending' / 'Pending signature' / any "pending…" → always yellow
    if (s.includes('PENDING')) return '#F59E0B';
    // Fall back to urgency-based color
    return getUrgencyColor(urgency);
  };

  // Define upcoming tasks from each transaction's upcomingTasksInWindow — every
  // incomplete item (stage/milestone) whose OWN due date falls in the dashboard's
  // yesterday/today/tomorrow window, evaluated independently by the backend. A
  // transaction can contribute zero, one, or several rows now: a stale item outside
  // the window is simply absent from this list, and never suppresses a different,
  // valid item within the window on the same transaction (unlike the old
  // nextActionWithCap, which picked a single representative item per transaction).
  const getUpcomingTasks = () => {
    if (transactions.length > 0) {
      const out = [];
      transactions.forEach((transaction) => {
        const itemsInWindow = transaction.upcomingTasksInWindow || [];
        itemsInWindow.forEach((nextAction) => {
          out.push({
            id: `${transaction.id}-${nextAction.id || nextAction.name}`,
            transactionId: transaction.id,
            transactionName: transaction.transactionName,
            address: transaction.propertyAddress || 'No address',
            city: transaction.propertyCity || '',
            state: transaction.propertyState || '',
            transactionType: transaction.type,
            transactionStatus: formatStatus(transaction.status),
            // NextAction details
            actionType: nextAction.type,
            actionName: nextAction.name,
            actionStatus: nextAction.status,
            dueLabel: nextAction.dueLabel || '',
            dueDate: nextAction.dueDate || '',
            daysRemaining: nextAction.daysRemaining,
            isOverdue: nextAction.isOverdue,
            urgency: nextAction.urgency,
            urgencyColor: getTaskDotColor(nextAction.urgency, nextAction.status),
            urgencyBgColor: getUrgencyBgColor(nextAction.urgency),
            borderColor: getTaskDotColor(nextAction.urgency, nextAction.status),
            // Party / user detail
            partyName: transaction.buyerName || transaction.sellerName || transaction.agentName || '',
            // For milestone type
            milestoneType: nextAction.milestoneType,
            // For prospect stage type
            stageOrder: nextAction.stageOrder,
            totalStages: nextAction.totalStages,
            side: nextAction.side
          });
        });
      });
      return out;
    }
    // No transactions yet
    return [];
  };

  const upcomingTasks = getUpcomingTasks();

  // Consolidated "Upcoming" list: merge tasks (nextAction) + events + service
  // requests into ONE flat date-sorted list; the transaction name rides inline
  // in the row title ("{title} - {transactionName}"), matching the original
  // Upcoming Tasks panel. Done items (COMPLETED/SKIPPED) are dropped; /upcoming
  // already returns only not-done events.
  const buildUpcomingItems = () => {
    // "today" is the viewer's OWN profile-state timezone day (e.g. California ->
    // Pacific), NOT the device's local day — so Overdue/Due decisions match what the
    // user actually sees on their calendar, even if their browser/device clock is in
    // another timezone. Mirrors the backend's profile-state resolution.
    const today = nowWallClockInViewerZone().localDate;
    const [ty, tm, td] = today.split('-').map(Number);
    const baseD = new Date(ty, tm - 1, td);
    const fmtLocal = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    // Dashboard window: only yesterday/today/tomorrow (today +/- 1 day), computed off
    // the viewer-zone "today" above. Tasks arrive pre-filtered to this window from the
    // backend (upcomingTasksInWindow, evaluated per-item independently — see
    // getUpcomingTasks above), and events arrive pre-filtered the same way from
    // calendar-service's getUpcomingEvents. This is a defensive re-check in case the
    // backend's "today" and this viewer-zone "today" ever disagree by a day — not the
    // primary enforcement point.
    const yesterdayD = new Date(baseD);
    yesterdayD.setDate(yesterdayD.getDate() - 1);
    const windowStart = fmtLocal(yesterdayD);
    const tomorrowD = new Date(baseD);
    tomorrowD.setDate(tomorrowD.getDate() + 1);
    const windowEnd = fmtLocal(tomorrowD);
    const outsideWindow = (date) => !date || date < windowStart || date > windowEnd;
    const formatDueShort = (dateStr) => {
      const d = new Date(`${dateStr}T00:00:00`);
      if (isNaN(d.getTime())) return dateStr;
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };
    // Not-done + past = "Overdue - {date}"; not-done + future = "Due {date}".
    const dueTextFor = (date, isOverdue) =>
      date ? (isOverdue ? `Overdue - ${formatDueShort(date)}` : `Due ${formatDueShort(date)}`) : '';

    // Event/SR transaction names resolve from the dashboard transactions first,
    // then the picker map (covers prospects + transactions beyond the 10-card limit).
    const nameFor = (id) => {
      if (!id) return '';
      const txn = transactions.find((t) => String(t.id) === String(id));
      return txn?.transactionName || txn?.propertyAddress || upcomingTxnNames[String(id)] || '';
    };

    const items = [];
    upcomingTasks.forEach((t) => {
      const status = String(t.actionStatus || '').toUpperCase();
      if (status === 'COMPLETED' || status === 'SKIPPED') return;
      const date = t.dueDate || '';
      const isOverdue = !!t.isOverdue || (!!date && date < today);
      items.push({
        key: `task-${t.id || t.transactionId}`,
        kind: 'task',
        title: t.actionName,
        date,
        status: status || 'PENDING',
        isOverdue,
        dueText: dueTextFor(date, isOverdue),
        assignee: t.partyName || '',
        dotColor: getTaskDotColor(t.urgency, t.actionStatus),
        transactionId: t.transactionId,
        transactionName: t.transactionName || '',
        color: '#4B3FA0',
      });
    });
    upcomingEvents.forEach((ev, i) => {
      const evStatus = String(ev.status || '').toUpperCase();
      if (evStatus === 'COMPLETED' || evStatus === 'CANCELLED') return;
      const isService = String(ev.category || '').toLowerCase() === 'service';
      const date = ev.scheduledDate || '';
      const isOverdue = !!date && date < today;
      items.push({
        key: `ev-${ev.eventId || i}`,
        kind: isService ? 'service' : 'event',
        title: ev.title,
        date,
        status: 'PENDING',
        isOverdue,
        dueText: dueTextFor(date, isOverdue),
        assignee: ev.createdByName || '',
        dotColor: getTaskDotColor(null, 'PENDING'),
        transactionId: ev.transactionId || null,
        transactionName: nameFor(ev.transactionId),
        color: isService ? '#f59e0b' : '#8b5cf6',
      });
    });

    const kindRank = { task: 0, event: 1, service: 2 };
    const dateKey = (d) => d || '9999-12-31';

    // Each item (task or event, linked to a transaction or standalone) is evaluated
    // independently against the yesterday/today/tomorrow window — no per-transaction
    // "pick one representative item" collapsing. A stale/missed item outside the
    // window is simply excluded and never suppresses a different, valid item within
    // the window on the same transaction (e.g. an overdue earlier stage no longer
    // hides a different, on-time later stage on that same transaction).
    const lineItems = items.filter((it) => !outsideWindow(it.date));

    const sorted = lineItems.sort(
      (a, b) =>
        dateKey(a.date).localeCompare(dateKey(b.date)) ||
        (kindRank[a.kind] - kindRank[b.kind]) ||
        String(a.title).localeCompare(String(b.title))
    );
    return sorted;
  };
  const upcomingItems = buildUpcomingItems();

  // Upcoming section is ready only when tasks (transactions fetch) AND
  // events/service-requests (calendar + picker fetches) are all finalized —
  // until then the section shows a loading indicator instead of partial rows.
  const upcomingReady = upcomingFetchDone && !isLoadingTransactions;

  // Bucket the already-filtered upcomingItems into Overdue (past dates) vs
  // This Week (today + tomorrow — the current dashboard fetch window).
  // "Same Upcoming Tasks data, grouped by Overdue / This week" per the UX
  // mockup — this is pure visual bucketing, no new fetches.
  const overdueItems = upcomingItems.filter((it) => it.isOverdue);
  const weekItems = upcomingItems.filter((it) => !it.isOverdue);
  const tasksFilterItems =
    tasksFilter === 'OVERDUE' ? overdueItems
    : tasksFilter === 'WEEK' ? weekItems
    : upcomingItems;
  const visibleUpcomingItems = tasksFilterItems.slice(0, upcomingVisibleCount);
  const upcomingFullyShown = upcomingVisibleCount >= tasksFilterItems.length;

  // First time the data finalises, pick the default tab per the mockup spec:
  // Overdue if any exist, else This Week. Guarded by a ref so manual tab
  // selection isn't clobbered on re-renders.
  useEffect(() => {
    if (!upcomingReady) return;
    if (tasksFilterDefaultedRef.current) return;
    tasksFilterDefaultedRef.current = true;
    setTasksFilter(overdueItems.length > 0 ? 'OVERDUE' : 'WEEK');
  }, [upcomingReady, overdueItems.length]);

  // Reset the Show-More window whenever the tab flips, so a user switching
  // from "All (8)" to "Overdue (5)" doesn't land on an empty "Show More".
  useEffect(() => {
    setUpcomingVisibleCount(5);
  }, [tasksFilter]);
  console.log(upcomingTasks.length+"==LENGHT==",upcomingTasks)
  // Get all transactions for display (for "View Details" section)
  const getAllTransactions = () => {
    if (transactions.length > 0) {
      return transactions.map((transaction) => ({
        id: transaction.id,
        transactionName: transaction.transactionName,
        transactionNumber: transaction.transactionNumber,
        address: transaction.propertyAddress || 'No address',
        city: transaction.propertyCity || '',
        state: transaction.propertyState || '',
        status: formatStatus(transaction.status),
        statusColor: getStatusColor(transaction.status),
        type: transaction.type,
        phase: transaction.phase,
        expectedClosingDate: transaction.expectedClosingDate,
        daysUntilClosing: transaction.daysUntilClosing,
        completionPercentage: transaction.completionPercentage
      }));
    }
    return [];
  };

  const allTransactions = getAllTransactions();

  const handleConnectDocusign = async () => {
    if (isConnectingDocusign) return;
    setIsConnectingDocusign(true);
    try {
      const getFirebaseToken = () => {
        return new Promise((resolve, reject) => {
          if (auth.currentUser) {
            auth.currentUser.getIdToken(true).then(resolve).catch(reject);
            return;
          }
          const timeoutId = setTimeout(() => {
            unsubscribe();
            reject(new Error('Firebase auth timeout'));
          }, 10000);
          const unsubscribe = auth.onAuthStateChanged(async (user) => {
            clearTimeout(timeoutId);
            unsubscribe();
            if (user) {
              try { resolve(await user.getIdToken(true)); } catch (e) { reject(e); }
            } else {
              reject(new Error('No authenticated user'));
            }
          });
        });
      };
      const firebaseToken = await getFirebaseToken();
      const response = await apiClient.get(API_ENDPOINTS.docusignConnect, {
        headers: { 'Authorization': `Bearer ${firebaseToken}` }
      });
      if (response.data?.connected === true) {
        setDocusignConnected(true);
        return;
      }
      const redirectUrl = response.data?.url || response.data?.redirectUrl || response.data?.authUrl;
      if (redirectUrl) {
        window.location.href = redirectUrl;
      } else {
        alert('DocuSign connection initiated. Please check your account.');
      }
    } catch (error) {
      console.error('Error connecting DocuSign:', error);
      alert('Failed to connect DocuSign. Please try again.');
    } finally {
      setIsConnectingDocusign(false);
    }
  };

  const handleDisconnectDocusign = async () => {
    if (isDisconnectingDocusign) return;
    setIsDisconnectingDocusign(true);
    try {
      const getFirebaseToken = () => {
        return new Promise((resolve, reject) => {
          if (auth.currentUser) {
            auth.currentUser.getIdToken(true).then(resolve).catch(reject);
            return;
          }
          const timeoutId = setTimeout(() => {
            unsubscribe();
            reject(new Error('Firebase auth timeout'));
          }, 10000);
          const unsubscribe = auth.onAuthStateChanged(async (user) => {
            clearTimeout(timeoutId);
            unsubscribe();
            if (user) {
              try { resolve(await user.getIdToken(true)); } catch (e) { reject(e); }
            } else {
              reject(new Error('No authenticated user'));
            }
          });
        });
      };
      const firebaseToken = await getFirebaseToken();
      await apiClient.delete(API_ENDPOINTS.docusignDisconnect, {
        headers: { 'Authorization': `Bearer ${firebaseToken}` }
      });
      setDocusignConnected(false);
    } catch (error) {
      console.error('Error disconnecting DocuSign:', error);
      alert('Failed to disconnect DocuSign. Please try again.');
    } finally {
      setIsDisconnectingDocusign(false);
    }
  };

  // DocuSign readiness gate — single-call status check before creating a transaction.
  // Every non-ready state still lets the user through via "Continue without DocuSign";
  // the backend is the final enforcement point. Dev-only bypass: skip the whole check and
  // navigate straight through (see isDevEnvironment above) — stg/prod keep this gate as-is.
  const handleCreateTransactionClick = async (destination) => {
    if (isDevEnvironment) {
      navigate(destination);
      return;
    }
    if (dsChecking) return;
    // Roles without DocuSign (buyer/seller etc.) skip the check and navigate directly
    if (!canSeeDocuSign) {
      navigate(destination);
      return;
    }
    setDsChecking(destination);
    try {
      const getFirebaseToken = () => {
        return new Promise((resolve, reject) => {
          if (auth.currentUser) {
            auth.currentUser.getIdToken(true).then(resolve).catch(reject);
            return;
          }
          const timeoutId = setTimeout(() => {
            unsubscribe();
            reject(new Error('Firebase auth timeout'));
          }, 10000);
          const unsubscribe = auth.onAuthStateChanged(async (user) => {
            clearTimeout(timeoutId);
            unsubscribe();
            if (user) {
              try { resolve(await user.getIdToken(true)); } catch (e) { reject(e); }
            } else {
              reject(new Error('No authenticated user'));
            }
          });
        });
      };
      const firebaseToken = await getFirebaseToken();

      const response = await apiClient.get(API_ENDPOINTS.docusignOidcStatus, {
        headers: { 'Authorization': `Bearer ${firebaseToken}` }
      });
      const data = response.data || {};
      const connected = data.connected === true;
      const inviteStatus = (data.inviteStatus || '').toLowerCase();
      // Mirrors mobile's hasFormAccess(): also treats a license-state default
      // provider (e.g. CAR for CA) as ready even if provisioned isn't explicitly true.
      // formProviders === null/undefined means the Rooms API was unreachable —
      // mirror mobile's useDocuSignReadiness and treat that as "unknown" (null)
      // rather than "no provider", so the gate fails open instead of blocking.
      // Read fresh from localStorage rather than the `userInfo` state — fetchUserData
      // populates userInfo only after several sequential awaits (token, /users/me,
      // 3-step DocuSign check, profile photo), so a click soon after page load could
      // otherwise see userInfo still null and intermittently lose the license-state
      // fallback, showing the "No Form Provider" popup even though a provider (e.g.
      // CAR) is genuinely connected.
      let licenseState;
      try {
        const u = JSON.parse(localStorage.getItem('user') || '{}');
        licenseState = u.licenseState || u.stateCode || u.state;
      } catch { /* ignore parse errors */ }
      let hasProvisionedProvider = data.formProviders == null
        ? null
        : hasFormAccess(data.formProviders, licenseState);

      // Fallback: some states (e.g. Colorado/CREC) have no form-provider integration
      // on this account at all — their forms are only exposed as a form library.
      // Only check this when the provider check definitively failed (not "unknown").
      if (hasProvisionedProvider === false) {
        const hasLibrary = await hasStateFormLibrary(licenseState, apiClient, API_ENDPOINTS, firebaseToken);
        if (hasLibrary) hasProvisionedProvider = true;
      }

      setDocusignConnected(connected);

      if (connected && inviteStatus === 'active' && hasProvisionedProvider !== false) {
        // Ready (or unknown, which fails open) — no popup, go straight to the creation page
        navigate(destination);
        return;
      }

      setDsModalDestination(destination);
      setDsModalRoomsUserId(data.roomsUserId || null);
      setDsResendSuccess(false);

      if (!connected) {
        setDsModal('not_connected');
      } else if (inviteStatus === 'pending') {
        setDsModal('pending');
      } else if (inviteStatus === 'active' && hasProvisionedProvider === false) {
        setDsModal('no_forms');
      } else {
        // Unrecognized invite status — allow through, backend will enforce
        navigate(destination);
      }
    } catch (err) {
      console.error('DocuSign status check failed:', err);
      // Allow navigation on unexpected error — backend will enforce
      navigate(destination);
    } finally {
      setDsChecking(null);
    }
  };

  const closeDsModal = () => {
    setDsModal(null);
    setDsResendSuccess(false);
  };

  const handleContinueWithoutDocusign = () => {
    const destination = dsModalDestination;
    closeDsModal();
    if (destination) navigate(destination);
  };

  const handleResendDsInvite = async () => {
    if (!dsModalRoomsUserId || dsResendingInvite) return;
    setDsResendingInvite(true);
    setDsResendSuccess(false);
    try {
      const getFirebaseToken = () => {
        return new Promise((resolve, reject) => {
          if (auth.currentUser) {
            auth.currentUser.getIdToken(true).then(resolve).catch(reject);
            return;
          }
          const timeoutId = setTimeout(() => {
            unsubscribe();
            reject(new Error('Firebase auth timeout'));
          }, 10000);
          const unsubscribe = auth.onAuthStateChanged(async (user) => {
            clearTimeout(timeoutId);
            unsubscribe();
            if (user) {
              try { resolve(await user.getIdToken(true)); } catch (e) { reject(e); }
            } else {
              reject(new Error('No authenticated user'));
            }
          });
        });
      };
      const firebaseToken = await getFirebaseToken();
      await apiClient.post(API_ENDPOINTS.docusignRoomsReinvite(dsModalRoomsUserId), {}, {
        headers: { 'Authorization': `Bearer ${firebaseToken}` }
      });
      setDsResendSuccess(true);
    } catch (err) {
      console.error('Resend invite failed:', err);
      alert('Failed to resend invitation. Please try again.');
    } finally {
      setDsResendingInvite(false);
    }
  };

  // Add Form Provider — opens DocuSign form provider setup URL from the readiness gate modal
  const handleGateAddFormProvider = async () => {
    if (dsAddingFormProvider) return;
    setDsAddingFormProvider(true);
    try {
      const getFirebaseToken = () => {
        return new Promise((resolve, reject) => {
          if (auth.currentUser) {
            auth.currentUser.getIdToken(true).then(resolve).catch(reject);
            return;
          }
          const timeoutId = setTimeout(() => {
            unsubscribe();
            reject(new Error('Firebase auth timeout'));
          }, 10000);
          const unsubscribe = auth.onAuthStateChanged(async (user) => {
            clearTimeout(timeoutId);
            unsubscribe();
            if (user) {
              try { resolve(await user.getIdToken(true)); } catch (e) { reject(e); }
            } else {
              reject(new Error('No authenticated user'));
            }
          });
        });
      };
      const firebaseToken = await getFirebaseToken();
      const response = await apiClient.get(API_ENDPOINTS.docusignFormProviderSetupUrl, {
        headers: { 'Authorization': `Bearer ${firebaseToken}` }
      });
      const url = response.data?.url;
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        alert('Setup URL not available. Please try again.');
      }
    } catch (err) {
      console.error('Failed to get form provider setup URL:', err);
      alert('Failed to open form provider setup. Please try again.');
    } finally {
      setDsAddingFormProvider(false);
    }
  };

  // Connect DocuSign from the readiness gate modal — reuses the same connect flow as the status banner
  const handleGateConnectDocusign = async () => {
    await handleConnectDocusign();
    closeDsModal();
  };

  // Define quick actions based on user role
  const getQuickActions = () => {
    if (userIsAgent) {
      // Agent-specific quick actions
      const actions = [];
      // Create transaction options — Agent and TC (TC creates on behalf of a named agent)
      if (userRole?.toUpperCase() === 'AGENT' || userRole?.toUpperCase() === 'TRANSACTION_COORDINATOR') {
        actions.push(
          {
            icon: <img src={listingIcon} alt="Transactions" />,
            label: dsChecking === '/listing-transaction' ? 'Checking DocuSign...' : 'Create Listing Transaction',
            onClick: () => handleCreateTransactionClick('/listing-transaction')
          },
          {
            icon: <img src={keyIcon} alt="Purchase" />,
            label: dsChecking === '/purchase-transaction' ? 'Checking DocuSign...' : 'Create Buying Transaction',
            onClick: () => handleCreateTransactionClick('/purchase-transaction')
          }
        );
      }
      actions.unshift({
        icon: <img src={transactionsIcon} alt="Listing" />,
        label: 'View My Active Transactions',
        onClick: () => navigate('/transactions', { state: { filter: 'active' } })
      });
      // Create SR is available to agents and TCs, but not brokers.
      if (userRole?.toUpperCase() !== 'BROKER') {
        actions.push({
          icon: <img src={serviceIcon} alt="Purchase" />,
          label: 'Create Service Request',
          onClick: () => navigate('/create-service-request')
        });
      }
      actions.push({
        icon: <img src={inviteIcon} alt="Invite" />,
        label: 'Invite Someone',
        onClick: () => navigate('/invite-someone')
      });
      return actions;
    } else {
      // Regular user quick actions (buyers, sellers)
      return [
        {
          icon: <img src={transactionsIcon} alt="Transactions" />,
          label: 'View My Active Transactions',
          onClick: () => navigate('/transactions', { state: { filter: 'prospect' } })
        },
        {
          icon: <img src={listingIcon} alt="Browse"  />,
          label: 'Browse Properties',
          onClick: () => navigate('/properties')
        },
        {
          icon: <img src={serviceIcon} alt="Create Service Request" />,
          label: 'Create Service Request',
          onClick: () => navigate('/create-service-request')
        },
        {
          icon: <img src={inviteIcon} alt="Invite" />,
          label: 'Invite Someone',
          onClick: () => navigate('/invite-someone')
        }
      ];
    }
  };

  const quickActions = getQuickActions();

  const handleWelcomeOk = () => {
    setShowWelcomePopup(false);
    setWelcomeData(null);
  };

  // DocuSign readiness gate modal content — mirrors mobile app's DocuSignReadinessModal variants
  const DS_GATE_CONFIG = {
    not_connected: {
      icon: 'link-off',
      title: 'DocuSign Not Connected',
      body: 'DocuSign helps you e-sign forms as part of the transaction. Connect now, or continue without it and connect later from Connected Services.',
      primaryLabel: 'Connect DocuSign',
      primaryLoadingLabel: 'Connecting...',
      primaryLoading: isConnectingDocusign,
      onPrimary: handleGateConnectDocusign,
    },
    pending: {
      icon: 'clock',
      title: 'Invitation Pending',
      body: 'Your DocuSign invitation is still pending. Accept it from your email to unlock e-signing, or continue without DocuSign for now.',
      primaryLabel: 'Resend Invite',
      primaryLoadingLabel: 'Resending...',
      primaryLoading: dsResendingInvite,
      onPrimary: handleResendDsInvite,
    },
    no_forms: {
      icon: 'file-alert',
      title: 'No Form Provider',
      body: "You don't have any form providers set up. Add one in Connected Services to load state-specific forms, or continue without one for now.",
      primaryLabel: 'Add Form Provider',
      primaryLoadingLabel: 'Opening...',
      primaryLoading: dsAddingFormProvider,
      onPrimary: handleGateAddFormProvider,
    },
  };
  const dsGateConfig = dsModal ? DS_GATE_CONFIG[dsModal] : null;

  // Computed once per render, reused by the scroll handler's active-date lookup.
  const riaMessageGroups = groupRiaMessagesByDay(riaMessages);
  riaGroupsRef.current = riaMessageGroups;

  // Compact "Your profile is N% complete" card — single-row (~64px) replacement
  // for the full checklist card shipped earlier. Shows the ring, a one-line
  // nudge (title + the top-of-queue checklist item's description), the primary
  // CTA for that item (navigates to /profile), and an X dismiss. Dismiss hides
  // the card for 7 days via localStorage; hits 100% complete → hidden
  // permanently (gate off profileStatus.isComplete, not the dismiss key).
  const renderProfileCompletionCard = () => {
    const ps = profileStatus;
    if (!ps || ps.isComplete) return null;
    // Dismissed? Respect the 7-day hide window.
    if (profileCardDismissedUntil) {
      const until = Date.parse(profileCardDismissedUntil);
      if (!Number.isNaN(until) && until > Date.now()) return null;
    }
    const pct = Math.max(0, Math.min(100, ps.completionPercentage ?? 0));
    const CIRC = 2 * Math.PI * 20; // r=20, ring sized for a 48×48 svg.
    const incomplete = (ps.checklist || []).filter((i) => !i.isComplete);
    const topItem = incomplete[0] || null;
    const ctaLabel = (ps.nextStep && ps.nextStep.label)
      || (topItem && topItem.label && `Add ${topItem.label.toLowerCase()}`)
      || 'Complete profile';
    const nudge = (topItem && topItem.description) || 'Finish your profile to continue.';
    const goToProfile = () => navigate('/profile');
    const dismissForSevenDays = (e) => {
      e.stopPropagation();
      const hideUntil = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();
      try { localStorage.setItem(PROFILE_CARD_DISMISS_KEY, hideUntil); } catch { /* ignore */ }
      setProfileCardDismissedUntil(hideUntil);
    };
    return (
      <div className="dashboard-profile-compact">
        <div className="dashboard-profile-compact-ring" aria-hidden="true">
          <svg width="48" height="48" viewBox="0 0 48 48">
            <circle cx="24" cy="24" r="20" stroke="#F3F0FB" strokeWidth="4" fill="none" />
            <circle
              cx="24" cy="24" r="20"
              stroke="#381F83" strokeWidth="4" fill="none" strokeLinecap="round"
              strokeDasharray={CIRC}
              strokeDashoffset={CIRC * (1 - pct / 100)}
              transform="rotate(-90 24 24)"
            />
          </svg>
          <span className="dashboard-profile-compact-pct">{pct}%</span>
        </div>
        <div className="dashboard-profile-compact-text">
          <div className="dashboard-profile-compact-title">
            Your profile is {pct}% complete
          </div>
          <div className="dashboard-profile-compact-subtitle">{nudge}</div>
        </div>
        <button type="button" className="dashboard-profile-compact-cta" onClick={goToProfile}>
          {ctaLabel}
        </button>
        <button
          type="button"
          className="dashboard-profile-compact-dismiss"
          onClick={dismissForSevenDays}
          aria-label="Dismiss for 7 days"
          title="Dismiss for 7 days"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>
    );
  };

  return (
    <div className="dashboard-exact-container">

      {/* Main Content */}
      <main className="dashboard-exact-main">
        {/* User Profile Card */}
        {/* userInfo && (
          <div className="dashboard-user-profile-card">
            <div className="dashboard-user-avatar">
              {userInfo.profilePhotoUrl || userInfo.profilePhotoBase64 ? (
                <img
                  src={userInfo.profilePhotoUrl || userInfo.profilePhotoBase64}
                  alt="Profile"
                  className="dashboard-user-avatar-img"
                />
              ) : (
                <span className="dashboard-user-avatar-initials">
                  {(userInfo.firstName?.[0] || '').toUpperCase()}
                  {(userInfo.lastName?.[0] || '').toUpperCase()}
                </span>
              )}
            </div>
            <div className="dashboard-user-info">
              <h2 className="dashboard-user-name">
                {userInfo.firstName} {userInfo.lastName}
                {userIsAgent && <span className="dashboard-user-role-badge">{userRole?.toUpperCase() === 'BROKER' ? 'Broker' : 'Agent'}</span>}
              </h2>
              <div className="dashboard-user-details">
                {userInfo.email && (
                  <span className="dashboard-user-detail">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                      <polyline points="22,6 12,13 2,6"/>
                    </svg>
                    {userInfo.email}
                  </span>
                )}
                {userInfo.phone && (
                  <span className="dashboard-user-detail">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                    </svg>
                    {userInfo.phone}
                  </span>
                )}
                {userIsAgent && userInfo.licenseNumber && (
                  <span className="dashboard-user-detail">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="4" width="18" height="16" rx="2"/>
                      <line x1="7" y1="9" x2="17" y2="9"/>
                      <line x1="7" y1="13" x2="12" y2="13"/>
                    </svg>
                    License: {userInfo.licenseNumber}
                  </span>
                )}
              </div>
              <div className="dashboard-user-verification">
                <span className={`dashboard-verification-badge ${userInfo.emailVerified ? 'verified' : 'unverified'}`}>
                  {userInfo.emailVerified ? (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  ) : (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/>
                      <line x1="12" y1="8" x2="12" y2="12"/>
                      <line x1="12" y1="16" x2="12.01" y2="16"/>
                    </svg>
                  )}
                  Email {userInfo.emailVerified ? 'Verified' : 'Not Verified'}
                </span>
                <span className={`dashboard-verification-badge ${userInfo.phoneVerified ? 'verified' : 'unverified'}`}>
                  {userInfo.phoneVerified ? (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  ) : (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/>
                      <line x1="12" y1="8" x2="12" y2="12"/>
                      <line x1="12" y1="16" x2="12.01" y2="16"/>
                    </svg>
                  )}
                  Phone {userInfo.phoneVerified ? 'Verified' : 'Not Verified'}
                </span>
              </div>
            </div>
          </div>
        )*/}

        {/* Greeting */}
        <h1 className="dashboard-exact-greeting" ref={greetingRef} style={{ marginLeft: `${greetingOffset}px` }}>
          Hi {userName}, what would you like to do today?
        </h1>

        {/* Search Box with Integrated Chat */}
        {isRiaChatExpanded && (
          <div className="ria-chat-backdrop" onClick={() => setIsRiaChatExpanded(false)} />
        )}
        <div className="dashboard-exact-search-container" ref={riaSectionRef}>
          <div className={`dashboard-exact-search-card${isRiaChatExpanded ? ' dashboard-exact-search-card--expanded' : ''}`}>
            <button
              className="dashboard-exact-expand-btn"
              onClick={() => setIsRiaChatExpanded(prev => !prev)}
              aria-label={isRiaChatExpanded ? 'Collapse Ria chat' : 'Expand Ria chat'}
              title={isRiaChatExpanded ? 'Collapse' : 'Expand'}
            >
              {isRiaChatExpanded ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="4 14 10 14 10 20"/>
                  <polyline points="20 10 14 10 14 4"/>
                  <line x1="14" y1="10" x2="21" y2="3"/>
                  <line x1="3" y1="21" x2="10" y2="14"/>
                </svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="15 3 21 3 21 9"/>
                  <polyline points="9 21 3 21 3 15"/>
                  <line x1="21" y1="3" x2="14" y2="10"/>
                  <line x1="3" y1="21" x2="10" y2="14"/>
                </svg>
              )}
            </button>

            {/* Chat messages area inside the card */}
            <div className="dashboard-ria-chat-area" ref={riaChatAreaRef} onScroll={handleRiaChatAreaScroll}>
              <div ref={riaTopSentinelRef} className="dashboard-ria-top-sentinel" />
              {riaActiveDateLabel && (
                <div className={`dashboard-ria-date-header${riaDatePillVisible ? ' dashboard-ria-date-header-visible' : ''}`}>
                  <span>{riaActiveDateLabel}</span>
                </div>
              )}
              {riaLoadingOlder && (
                <div className="dashboard-ria-loading-older"><span className="dashboard-ria-spinner" /></div>
              )}
              {riaReachedHistoryStart && !riaLoadingOlder && (
                <div className="dashboard-ria-history-start">You've reached the beginning of your conversation history</div>
              )}
              {riaLoadingHistory && riaMessages.length === 0 && (
                <div className="dashboard-ria-loading-older dashboard-ria-loading-initial">
                  <span className="dashboard-ria-spinner" />
                  <span>Loading your conversation...</span>
                </div>
              )}
              {riaMessageGroups.map((group) => (
                <div
                  key={group.dateKey}
                  className="dashboard-ria-day-group"
                  ref={(el) => {
                    if (el) riaDayGroupNodesRef.current.set(group.dateKey, el);
                    else riaDayGroupNodesRef.current.delete(group.dateKey);
                  }}
                >
                  {group.items.map((msg, index) => (
                    <React.Fragment key={index}>
                    <div
                      className={`dashboard-ria-message ${msg.role === 'user' ? 'dashboard-ria-message-user' : 'dashboard-ria-message-ria'}`}
                    >
                      {msg.role === 'ria' && (
                        <div className="dashboard-ria-avatar">
                          <img src={riaLogo} alt="Ria" className="dashboard-ria-avatar-img" />
                        </div>
                      )}
                      <div className="dashboard-ria-message-bubble">
                        {msg.role === 'user' && (
                          <span className="dashboard-ria-message-sender">
                            {userName}, {msg.time}
                          </span>
                        )}
                        {msg.role === 'ria' && (
                          <span className="dashboard-ria-message-sender">
                            Ria, {msg.time}
                          </span>
                        )}
                        <div className="dashboard-ria-message-content">
                          {msg.role === 'ria' ? formatRiaMessage(msg.content) : msg.content}
                          {msg.role === 'ria' && msg.noteLinks?.length > 0 && (
                            <RiaNoteLinks notes={msg.noteLinks} />
                          )}
                          {msg.role === 'ria' && msg.cmaReportLink && (
                            <CmaReportLink link={msg.cmaReportLink} />
                          )}
                        </div>
                      </div>
                      {msg.role === 'user' && (
                        <div className="dashboard-ria-user-avatar">
                          {userInfo?.profilePhotoBase64 || userInfo?.profilePhotoUrl ? (
                            <img
                              src={userInfo.profilePhotoBase64 || userInfo.profilePhotoUrl}
                              alt="User"
                              className="dashboard-ria-user-avatar-img"
                            />
                          ) : (
                            <span className="dashboard-ria-user-avatar-initials">
                              {(userInfo?.firstName?.[0] || '').toUpperCase()}
                              {(userInfo?.lastName?.[0] || '').toUpperCase()}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    {msg.role === 'ria' && msg.properties && msg.properties.length > 0 && (
                      <div className="dashboard-ria-property-cards">
                        <div className="dashboard-ria-property-cards-scroll">
                          {msg.properties.map((prop) => (
                            <div
                              key={prop.id || prop.mlsId}
                              className="dashboard-ria-property-card"
                              onClick={() => navigate(`/property/${prop.mlsId || prop.id}`)}
                            >
                              <div className="dashboard-ria-property-card-image-wrap">
                                {prop.primaryPhoto ? (
                                  <img
                                    src={prop.primaryPhoto}
                                    alt={prop.address}
                                    className="dashboard-ria-property-card-image"
                                  />
                                ) : (
                                  <div className="dashboard-ria-property-card-placeholder">
                                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#B0A8D0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/></svg>
                                  </div>
                                )}
                                {prop.status && (
                                  <span
                                    className="dashboard-ria-property-card-status"
                                    style={{ backgroundColor: prop.statusColor || '#2E7D32' }}
                                  >
                                    {prop.status}
                                  </span>
                                )}
                                <span className="dashboard-ria-property-card-price">
                                  {formatRiaPropertyPrice(prop.price)}
                                </span>
                              </div>
                              <div className="dashboard-ria-property-card-body">
                                <div className="dashboard-ria-property-card-address">{prop.address}</div>
                                <div className="dashboard-ria-property-card-location">
                                  {prop.city}{prop.city && prop.state ? ', ' : ''}{prop.state} {prop.zipCode}
                                </div>
                                <div className="dashboard-ria-property-card-specs">
                                  <span>{prop.beds} bd</span>
                                  <span className="dashboard-ria-property-card-divider">|</span>
                                  <span>{prop.baths} ba</span>
                                  <span className="dashboard-ria-property-card-divider">|</span>
                                  <span>{(prop.sqft || 0).toLocaleString()} sqft</span>
                                </div>
                                <button
                                  type="button"
                                  className="dashboard-ria-property-card-btn"
                                  onClick={(e) => { e.stopPropagation(); navigate(`/property/${prop.mlsId || prop.id}`); }}
                                >
                                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                                  View Details
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    </React.Fragment>
                  ))}
                </div>
              ))}
              {riaLoading && (
                <div className="dashboard-ria-message dashboard-ria-message-ria">
                  <div className="dashboard-ria-avatar">
                    <img src={riaLogo} alt="Ria" className="dashboard-ria-avatar-img" />
                  </div>
                  <div className="dashboard-ria-message-content dashboard-ria-typing">
                    <span></span><span></span><span></span>
                  </div>
                </div>
              )}
              <div ref={riaMessagesEndRef} />
            </div>

            {/* Search bar at bottom of card */}
            <AskRiaInputBar
              value={searchQuery}
              onChange={setSearchQuery}
              onSend={handleAskRia}
              placeholder="Ask Ria..."
              loading={riaLoading}
            />

            {/* Tip lives INSIDE the Ria card now (was previously a sibling
                below the card border, which read as a stray caption). */}
            <div className="dashboard-exact-search-tip">
              <img src={bulbIcon} alt="tip" width="16" height="16" />
              <span>Tip: You can say "Ria, show me pending inspections"</span>
            </div>
          </div>
        </div>

        {/* Complete Your Profile — parity with SP dashboard's onboarding card.
            Renders only when /api/v1/dashboard's profileStatus.isComplete
            is false (backend calculates checklist per role — photo +
            personal info + license + brokerage for AGENT/TC/BROKER). */}
        {renderProfileCompletionCard()}

        {/* Tasks and Quick Actions Grid */}
        <div className="dashboard-exact-content-grid main">
          {/* Upcoming — flat list: tasks + events + service requests, date-sorted.
              Every item is an independent row (the type chip identifies the kind).
              Shows 5 rows until "Show More"; a loading indicator gates the section
              until all sources (tasks, events, service requests) are finalized. */}
          <div className="dashboard-exact-section inner1">
            <div className="dashboard-upcoming-header">
              <h2 className="dashboard-exact-section-title">Upcoming Tasks</h2>
              {upcomingReady && upcomingItems.length > 0 && (
                <div className="dashboard-upcoming-tabs" role="tablist" aria-label="Upcoming task bucket">
                  {[
                    { key: 'OVERDUE', label: 'Overdue', count: overdueItems.length },
                    { key: 'WEEK',    label: 'This week', count: weekItems.length },
                    { key: 'ALL',     label: 'All',   count: upcomingItems.length },
                  ].map((tab) => (
                    <button
                      key={tab.key}
                      type="button"
                      role="tab"
                      aria-selected={tasksFilter === tab.key}
                      className={`dashboard-upcoming-tab${tasksFilter === tab.key ? ' dashboard-upcoming-tab--active' : ''}`}
                      onClick={() => setTasksFilter(tab.key)}
                    >
                      {tab.label} ({tab.count})
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="dashboard-exact-tasks-list">
              {!upcomingReady ? (
                <div className="dashboard-upcoming-loading">
                  Loading
                  <span className="dashboard-upcoming-loading-dot">.</span>
                  <span className="dashboard-upcoming-loading-dot">.</span>
                  <span className="dashboard-upcoming-loading-dot">.</span>
                </div>
              ) : tasksFilterItems.length > 0 ? (
                <>
                  {visibleUpcomingItems.map((item) => (
                  <div
                    className="dashboard-exact-task-item"
                    key={item.key}
                    style={{ borderLeftColor: item.color, cursor: item.transactionId ? 'pointer' : 'default' }}
                    onClick={() => item.transactionId && navigate(`/transaction/${item.transactionId}`)}
                  >
                    <div className="dashboard-exact-task-content">
                      <div className="dashboard-exact-task-title">
                        {/* Kind chip hidden for 'task' — the section header already
                            says "Upcoming Tasks" so the chip is redundant for the
                            majority case. Services/events still get their chip so
                            the mixed list stays identifiable. Per UX mockup dev
                            note: "Removed TASK tag and NOT_STARTED label". */}
                        {item.kind !== 'task' && (
                          <span className={`dashboard-upcoming-chip dashboard-upcoming-chip--${item.kind}`}>
                            {item.kind === 'service' ? 'Service' : 'Event'}
                          </span>
                        )}
                        {item.title}
                        {item.transactionName && <span className="dashboard-exact-task-address"> - {item.transactionName}</span>}
                      </div>
                      {/* Status label (NOT_STARTED / PENDING) removed per the UX
                          mockup — the dueText below ("Overdue - Oct 3" / "Due
                          Oct 5") already conveys actionable state. */}
                      <div className="dashboard-exact-task-meta-stack">
                        {item.dueText && (
                          <span className="dashboard-exact-task-due">{item.dueText}</span>
                        )}
                        {item.assignee && (
                          <span className="dashboard-exact-task-assigned">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                            </svg>
                            {item.assignee}
                          </span>
                        )}
                      </div>
                      {item.transactionId && (
                        <div className="dashboard-exact-task-actions">
                          {/* "+ Ask Ria" composes a contextual prompt
                              ("Help me with {title} — {txn}") and scrolls up
                              to the Ria chat card so the user watches the
                              reply land, instead of opening a route or draft
                              box. Disabled while a Ria request is in flight
                              so rapid clicks don't queue multiple prompts. */}
                          <button
                            type="button"
                            className="dashboard-exact-task-action-ria"
                            onClick={(e) => {
                              e.stopPropagation();
                              askRiaAboutTask(item);
                            }}
                            disabled={riaLoading}
                            title={riaLoading ? 'Ria is thinking…' : `Ask Ria about ${item.title}`}
                          >
                            + Ask Ria
                          </button>
                          <button
                            className="dashboard-exact-task-action"
                            style={{
                              backgroundColor: 'rgba(217, 213, 236, 0.7)',
                              color: '#381F83',
                              borderRadius: '20px',
                              border: '1px solid rgba(75, 63, 160, 0.15)'
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/transaction/${item.transactionId}`);
                            }}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                              <circle cx="12" cy="12" r="3"/>
                            </svg>
                            View
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  ))}
                  {tasksFilterItems.length > 5 && (
                    <button
                      type="button"
                      className="dashboard-upcoming-showmore"
                      onClick={() =>
                        setUpcomingVisibleCount((prev) =>
                          prev >= tasksFilterItems.length ? 5 : Math.min(prev + 5, tasksFilterItems.length)
                        )
                      }
                    >
                      {upcomingFullyShown
                        ? 'Show Less'
                        : `Show More (${Math.min(5, tasksFilterItems.length - upcomingVisibleCount)})`}
                    </button>
                  )}
                </>
              ) : upcomingItems.length > 0 ? (
                // The user has upcoming items, just none in the selected bucket.
                // Keep this message simple so the user knows to switch tabs
                // rather than thinking the dashboard is broken.
                <div className="dashboard-exact-empty-state">
                  <h3 className="dashboard-exact-empty-title">
                    {tasksFilter === 'OVERDUE' ? 'Nothing overdue' : 'Nothing due this week'}
                  </h3>
                  <p className="dashboard-exact-empty-description">
                    Switch to {tasksFilter === 'OVERDUE' ? '"This week"' : '"Overdue"'} or "All" to see your other items.
                  </p>
                </div>
              ) : (
                <div className="dashboard-exact-empty-state">
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="1.5">
                    <path d="M9 11l3 3L22 4"/>
                    <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/>
                  </svg>
                  <h3 className="dashboard-exact-empty-title">No Upcoming Items</h3>
                  <p className="dashboard-exact-empty-description">
                    Tasks, events, and service requests from your transactions will appear here.
                  </p>
                  {(userRole?.toUpperCase() === 'AGENT' || userRole?.toUpperCase() === 'TRANSACTION_COORDINATOR') && (
                    <button className="dashboard-exact-empty-action" onClick={() => navigate('/listing-transaction')}>
                      Create a new transaction
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>


          {/* Quick Actions */}
          <div className="dashboard-exact-section inner2">
            <h2 className="dashboard-exact-section-title">Quick Actions</h2>
            <div className="dashboard-exact-actions-list">
              {quickActions.map((action, index) => (
                <button
                  className="dashboard-exact-action-btn"
                  key={index}
                  onClick={action.onClick}
                  disabled={!!dsChecking}
                >
                  <span className="dashboard-exact-action-icon">{action.icon}</span>
                  <span className="dashboard-exact-action-label">{action.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* DocuSign Status Card — agents only, hidden once fully set up */}
        {docuSignBanner && (
          <div className="dashboard-ds-card DSCard">
            <div className="dashboard-ds-card-header">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
              </svg>
              <span>DocuSign Setup</span>
            </div>

            {/* Loading */}
            {docuSignBanner === 'loading' && (
              <div className="dashboard-ds-banner dashboard-ds-banner--loading">
                <div className="dashboard-ds-spinner" />
                <div className="dashboard-ds-banner-content">
                  <span className="dashboard-ds-banner-title">Checking DocuSign setup...</span>
                </div>
              </div>
            )}

            {/* Not Connected */}
            {docuSignBanner === 'not_connected' && (
              <div className="dashboard-ds-banner">
                <div className="dashboard-ds-banner-icon dashboard-ds-banner-icon--warning">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                    <line x1="2" y1="2" x2="22" y2="22" strokeWidth="2.5"/>
                  </svg>
                </div>
                <div className="dashboard-ds-banner-content">
                  <span className="dashboard-ds-banner-title">DocuSign Not Connected</span>
                  <span className="dashboard-ds-banner-subtitle">DocuSign connection required to for forms</span>
                </div>
                <button
                  className="dashboard-ds-connect-btn"
                  onClick={handleConnectDocusign}
                  disabled={isConnectingDocusign}
                >
                  <img src={docusignIcon} alt="DocuSign" className="dashboard-ds-connect-btn-icon" />
                  <span>{isConnectingDocusign ? 'Connecting...' : 'Connect Your DocuSign Account'}</span>
                </button>
              </div>
            )}

            {/* Invite Pending */}
            {docuSignBanner === 'pending' && (
              <div className="dashboard-ds-banner dashboard-ds-banner--warn">
                <div className="dashboard-ds-banner-icon dashboard-ds-banner-icon--pending">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/>
                    <polyline points="12 6 12 12 16 14"/>
                  </svg>
                </div>
                <div className="dashboard-ds-banner-content">
                  <span className="dashboard-ds-banner-title">DocuSign Invite Pending</span>
                  <span className="dashboard-ds-banner-subtitle">Accept the invite sent to your email</span>
                </div>
              </div>
            )}

            {/* No Form Libraries */}
            {docuSignBanner === 'no_forms' && (
              <div
                className="dashboard-ds-banner dashboard-ds-banner--action dashboard-ds-banner--warn"
                onClick={() => navigate('/connected-services')}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && navigate('/connected-services')}
              >
                <div className="dashboard-ds-banner-icon dashboard-ds-banner-icon--pending">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                    <line x1="12" y1="18" x2="12.01" y2="18" strokeWidth="3"/>
                    <line x1="12" y1="12" x2="12" y2="15"/>
                  </svg>
                </div>
                <div className="dashboard-ds-banner-content">
                  <span className="dashboard-ds-banner-title">No Form Libraries</span>
                  <span className="dashboard-ds-banner-subtitle">Add a form provider to create transactions</span>
                </div>
                <svg className="dashboard-ds-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 18 15 12 9 6"/>
                </svg>
              </div>
            )}
          </div>
        )}

        {/* All Transactions Section */}
        {allTransactions.length > 0 && (
          <div className="dashboard-exact-section" style={{ marginTop: '24px', display: 'none' }}>
            <h2 className="dashboard-exact-section-title">My Transactions</h2>
            <div className="dashboard-exact-tasks-list">
              {allTransactions.map((transaction, index) => (
                <div
                  className="dashboard-exact-task-item"
                  key={transaction.id || index}
                  style={{ borderLeftColor: transaction.statusColor, cursor: 'pointer' }}
                  onClick={() => navigate(`/transaction/${transaction.id}`)}
                >
                  <div className="dashboard-exact-task-content">
                    <div className="dashboard-exact-task-title">
                      {transaction.type === 'SALE' || transaction.type === 'LISTING' ? 'Listing' : 'Purchase'}
                      <span className="dashboard-exact-task-address"> - {transaction.transactionName}</span>
                    </div>
                    <span className="dashboard-exact-task-status">
                      <span
                        className="dashboard-exact-status-dot"
                        style={{ backgroundColor: transaction.statusColor }}
                      ></span>
                      {transaction.status}
                    </span>
                    <span className="dashboard-exact-task-due">
                      {transaction.expectedClosingDate ? `Close: ${transaction.expectedClosingDate}` : 'No closing date'}
                    </span>
                      {/* {transaction.daysUntilClosing !== undefined && (
                        <span className="dashboard-exact-task-assigned">
                          {transaction.daysUntilClosing} days
                        </span>
                      )}
                      {transaction.completionPercentage !== undefined && (
                        <span
                          style={{
                            backgroundColor: '#E0E7FF',
                            color: '#381F83',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontWeight: '600'
                          }}
                        >
                          {transaction.completionPercentage}% Complete
                        </span>
                      )} */}
                    <button
                      className="dashboard-exact-task-action"
                      style={{
                        backgroundColor: '#E0E7FF',
                        color: '#381F83',
                        marginLeft: 'auto'
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/transaction/${transaction.id}`);
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </svg>
                      View Details
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>


      {/* Welcome Popup for Transaction Invite */}
      {showWelcomePopup && welcomeData && (
        <div className="dashboard-welcome-overlay" onClick={handleWelcomeOk}>
          <div className="dashboard-welcome-popup" onClick={(e) => e.stopPropagation()}>
            <h2 className="dashboard-welcome-title">Welcome!</h2>
            <p className="dashboard-welcome-message">
              You have successfully joined the transaction for{' '}
              <strong>
                {[welcomeData.propertyAddress, welcomeData.city, welcomeData.state].filter(Boolean).join(', ')}
              </strong>
              {welcomeData.role ? ` as ${welcomeData.role}` : ''}.
            </p>
            <button className="dashboard-welcome-ok-btn" onClick={handleWelcomeOk}>
              OK
            </button>
          </div>
        </div>
      )}

      {/* DocuSign Readiness Gate Modal — shown before Create Listing/Buying Transaction when not fully set up.
          Mirrors mobile app's DocuSignReadinessModal: icon badge, title, body, then a stacked
          filled-primary / outlined-secondary / plain-text-cancel button pattern. */}
      {dsModal && dsGateConfig && (
        <div className="dashboard-ds-gate-overlay" onClick={closeDsModal}>
          <div className="dashboard-ds-gate-modal" onClick={(e) => e.stopPropagation()}>
            <div className="dashboard-ds-gate-icon-badge">
              {dsGateConfig.icon === 'link-off' && (
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#381F83" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                  <line x1="2" y1="2" x2="22" y2="22"/>
                </svg>
              )}
              {dsGateConfig.icon === 'clock' && (
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#381F83" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12 6 12 12 16 14"/>
                </svg>
              )}
              {dsGateConfig.icon === 'file-alert' && (
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#381F83" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                  <line x1="12" y1="18" x2="12.01" y2="18" strokeWidth="3"/>
                  <line x1="12" y1="12" x2="12" y2="15"/>
                </svg>
              )}
            </div>

            <h3 className="dashboard-ds-gate-title">{dsGateConfig.title}</h3>
            <p className="dashboard-ds-gate-message">{dsGateConfig.body}</p>
            {dsModal === 'pending' && dsResendSuccess && (
              <p className="dashboard-ds-gate-success">Invitation resent successfully.</p>
            )}

            <button className="dashboard-ds-gate-btn-secondary" onClick={handleContinueWithoutDocusign}>
              Continue without DocuSign
            </button>

            <div className="dashboard-ds-gate-actions">
              <button className="dashboard-ds-gate-btn-cancel" onClick={closeDsModal}>
                Cancel
              </button>
              <button
                className="dashboard-ds-gate-btn-primary"
                onClick={dsGateConfig.onPrimary}
                disabled={dsGateConfig.primaryLoading}
              >
                {dsGateConfig.primaryLoading ? (
                  <span className="dashboard-ds-gate-spinner" />
                ) : (
                  dsGateConfig.primaryLabel
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardPage;
